import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { formatLocalDate } from '@/lib/dateUtils';
import {
  Users, Building2, Baby,   Calendar, CalendarX2, Camera, MessageSquare,
  Bell, ChevronRight, ChevronLeft, ChevronDown, Loader2, Sun, Moon, Sunrise,
  Utensils, Plus, AlertCircle, Edit2, DollarSign, Check, UserPlus,
  Send, ArrowUp, Trash2, Coins, Clock, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import StatsCard from '@/components/ui/StatsCard';
import QuickAction from '@/components/ui/QuickAction';
import EmptyState from '@/components/ui/EmptyState';
import OfflineIndicator from '@/components/ui/OfflineIndicator';
import SpeechToTextButton from '@/components/SpeechToTextButton';
import { useLanguage } from '@/components/LanguageContext';
import { useTheme } from '@/lib/ThemeContext';
import { getRoleLabel } from '@/lib/roleLabels';
import { toast } from 'sonner';
import ImageCropDialog from '@/components/ImageCropDialog';
import ClosuresCalendar from '@/components/ClosuresCalendar';
import MonthlySummary from '@/components/MonthlySummary';
import WeeklySummary from '@/components/WeeklySummary';
import ChildCarousel from '@/components/ChildCarousel';
import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard';
import { cn } from '@/lib/utils';
import confetti from 'canvas-confetti';
import {
  BACKGROUND_PRESETS,
  BACKGROUND_PRESET_LIST,
  getBackgroundStyle,
  getGradientClasses,
} from '@/components/ChildCardBackgrounds';
import { useChildBackgrounds } from '@/hooks/useChildBackgrounds';
import { useChildViewMode } from '@/hooks/useChildViewMode';
import { useEnabledModulesForUser } from '@/hooks/useBranchModules';
import {
  useEnrollableCostElements,
  useChildEnrollments,
  useEnrollChild,
  useUnenrollChild,
  useCancelUnenrollment,
  calculateMonthlyEstimate,
  getScheduleDescription
} from '@/hooks/useAccounting';

// Dietary options: key (stored in DB) -> Polish label (displayed)
const DIETARY_OPTIONS = [
  { key: 'Gluten-free', label: 'Bez glutenu' },
  { key: 'Lactose-free', label: 'Bez laktozy' },
  { key: 'Vegetarian', label: 'Wegetarianin' },
  { key: 'Vegan', label: 'Weganin' },
  { key: 'Nut allergy', label: 'Alergia - orzechy' },
  { key: 'Egg allergy', label: 'Alergia - jaja kurze' },
  { key: 'Fish allergy', label: 'Alergia - ryby' },
  { key: 'No pork', label: 'Bez wieprzowiny' }
];

// Helper to get Polish label for a dietary restriction key
const getDietaryLabel = (key) => {
  const option = DIETARY_OPTIONS.find(opt => opt.key === key);
  return option ? option.label : key;
};

// Import Supabase with session helpers
import { supabase, forceRefreshSession, withQueryTimeout, getFunctionErrorMessage } from '@/lib/supabase';
import { wouldExceedFreemiumLimit } from '@/lib/freemiumLimit';

// Import photo upload function
import { uploadAvatarPhoto } from '@/hooks/usePhotos';

// Import message hooks
import {
  useConversationMessages,
  useSendMessage,
  useMarkMessagesAsRead,
  useDeleteConversation
} from '@/hooks/useMessages';

// Import Kreator components
import KreatorBanner from '@/components/kreator/KreatorBanner';
import { useKreatorProgress } from '@/hooks/useKreatorProgress';
import { logger } from '@/lib/logger';
import { useReportAbsence, useCancelAbsence } from '@/hooks/useAbsences';
import { useDailyPlan, useTimeframes } from '@/hooks/useDailyPlans';
import { seedDefaultDailyPlanForGroup } from '@/lib/defaultDailyPlan';

export default function Home() {
  const { t } = useLanguage();
  const { user, userProfile, isAuthenticated, isLoading: authLoading, updatePreferences } = useAuth();
  const navigate = useNavigate();
  const reportAbsenceMutation = useReportAbsence();
  const cancelAbsenceMutation = useCancelAbsence();
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const loadRequestIdRef = useRef(0); // Track request IDs to cancel stale requests
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false);
  const [selectedChildForAbsence, setSelectedChildForAbsence] = useState(null);
  const [absenceToCancel, setAbsenceToCancel] = useState(null); // { id, absence_date, childName } when confirm modal is open
  // Use format() for local timezone consistency with DailyUpdates.jsx
  const [absenceDate, setAbsenceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [absenceReason, setAbsenceReason] = useState('');
  const [savingAbsence, setSavingAbsence] = useState(false);

  // New message dialog state (quick message from child card)
  const [newMessageDialogOpen, setNewMessageDialogOpen] = useState(false);
  const [selectedChildForMessage, setSelectedChildForMessage] = useState(null);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Parent date navigation - use format() for local timezone consistency
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  // Admin Dialog (for super_admin) - creates branch_admin
  const [addTenantAdminDialogOpen, setAddTenantAdminDialogOpen] = useState(false);
  const [tenantAdminForm, setTenantAdminForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    branch_id: ''
  });
  const [savingTenantAdmin, setSavingTenantAdmin] = useState(false);

  // Add Tenant Dialog (for super_admin)
  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false);

  // Cleanup orphaned users state
  const [cleaningUpUsers, setCleaningUpUsers] = useState(false);
  const [tenantForm, setTenantForm] = useState({
    schema_type: 'single_branch',
    tenant_name: '',
    tenant_address: '',
    tenant_contact_first_name: '',
    tenant_contact_last_name: '',
    tenant_contact_email: '',
    tenant_contact_phone: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_phone: '',
    subscription_type: 'standard'
  });
  const [savingTenant, setSavingTenant] = useState(false);

  // Add Group Dialog (for branch_admin)
  const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({
    name: '',
    picture_url: '',
    color_scheme: '#3b82f6', // Default blue
    main_teacher_id: '',
    secondary_teacher_ids: [],
    child_ids: []
  });
  const [selectedGroupImage, setSelectedGroupImage] = useState(null);
  const [cropGroupDialogOpen, setCropGroupDialogOpen] = useState(false);
  const [uploadingGroupPhoto, setUploadingGroupPhoto] = useState(false);
  const [savingGroup, setSavingGroup] = useState(false);

  // Branch switcher (for branch_admin with multiple branches)
  const [branches, setBranches] = useState([]);
  const [currentBranch, setCurrentBranch] = useState(null);

  // Add Child Dialog (for branch_admin)
  const [addChildDialogOpen, setAddChildDialogOpen] = useState(false);
  const [childForm, setChildForm] = useState({
    child_first_name: '',
    child_last_name: '',
    date_of_birth: '',
    pesel: '',
    group_id: '',
    parent_first_name: '',
    parent_last_name: '',
    parent_email: '',
    parent_phone: ''
  });
  const [selectedChildImage, setSelectedChildImage] = useState(null);
  const [cropChildDialogOpen, setCropChildDialogOpen] = useState(false);
  const [uploadingChildPhoto, setUploadingChildPhoto] = useState(false);
  const [savingChild, setSavingChild] = useState(false);

  // Announcement Dialog (for parents)
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  // Conversation Sheet (for parents) - slide up from bottom
  const [chatSheetOpen, setChatSheetOpen] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const messagesContainerRef = useRef(null);

  // Background picker dialog (for parents)
  const [backgroundPickerOpen, setBackgroundPickerOpen] = useState(false);
  const [backgroundPickerChild, setBackgroundPickerChild] = useState(null);

  // Active child index for carousel (for calendar color sync)
  const [activeChildIndex, setActiveChildIndex] = useState(0);

  // Child card backgrounds hook
  const { getBackground, setBackground } = useChildBackgrounds(user?.id, userProfile?.preferences, updatePreferences);

  // Child card view mode hook (Oceny/Dieta/Zapisy)
  const { getViewMode, setViewMode } = useChildViewMode(user?.id);

  // Module visibility (for parent - hide Zapisy when rozliczenia off)
  const { isModuleEnabled } = useEnabledModulesForUser();
  const rozliczeniaEnabled = isModuleEnabled('rozliczenia');
  const chatEnabled = isModuleEnabled('chat');

  const { getActiveColors } = useTheme();
  const themeColors = getActiveColors();

  // Kreator progress (for branch_admin wizard)
  const { data: kreatorProgress } = useKreatorProgress(currentBranch?.id);

  // Badge celebration dialog (parent child card – centered celebratory message)
  const [badgeCelebration, setBadgeCelebration] = useState(null);

  // Dietary edit dialog state
  const [dietaryDialogOpen, setDietaryDialogOpen] = useState(false);
  const [dietaryDialogChild, setDietaryDialogChild] = useState(null);
  const [dietaryFormData, setDietaryFormData] = useState({
    dietary_restrictions: [],
    dietary_notes: ''
  });
  const [savingDietary, setSavingDietary] = useState(false);

  // Parent child avatar editing state
  const [selectedChildForAvatarEdit, setSelectedChildForAvatarEdit] = useState(null);
  const [childAvatarImage, setChildAvatarImage] = useState(null);
  const [cropChildAvatarDialogOpen, setCropChildAvatarDialogOpen] = useState(false);
  const [uploadingChildAvatar, setUploadingChildAvatar] = useState(false);

  // Enrollment mutations
  const enrollChild = useEnrollChild();
  const unenrollChild = useUnenrollChild();
  const cancelUnenrollment = useCancelUnenrollment();
  const [enrollmentLoading, setEnrollmentLoading] = useState({});

  // Swipe gesture state for date navigation
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [dateTransition, setDateTransition] = useState({ direction: null, phase: 'idle' }); // phase: 'idle' | 'exit' | 'enter'
  const minSwipeDistance = 50;

  // Helper to change date with animation
  const changeDate = (newDate, direction) => {
    if (dateTransition.phase !== 'idle') return;

    // Phase 1: Exit animation
    setDateTransition({ direction, phase: 'exit' });

    // Phase 2: Change date and enter animation
    setTimeout(() => {
      setSelectedDate(newDate);
      setDateTransition({ direction, phase: 'enter' });

      // Phase 3: Reset to idle
      setTimeout(() => {
        setDateTransition({ direction: null, phase: 'idle' });
      }, 200);
    }, 150);
  };

  // Conversation messages hook
  const {
    data: conversationMessagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: messagesLoading,
    refetch: refetchMessages
  } = useConversationMessages(selectedConversation?.id, {
    enabled: !!selectedConversation?.id && chatSheetOpen
  });

  // Today's date string for comparisons (local timezone)
  const todayStr = format(new Date(), 'yyyy-MM-dd');

  // Flatten messages from all pages and reverse for display (oldest first)
  const conversationMessages = useMemo(() => {
    if (!conversationMessagesData?.pages) return [];
    const allMessages = conversationMessagesData.pages.flatMap(page => page.messages);
    return [...allMessages].reverse();
  }, [conversationMessagesData]);

  // Message mutations
  const sendMessage = useSendMessage();
  const markMessagesAsRead = useMarkMessagesAsRead();
  const deleteConversation = useDeleteConversation();

  // Delete conversation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Redirect teachers to TeacherDashboard
  useEffect(() => {
    if (userProfile && isAuthenticated) {
      const role = userProfile.active_role || userProfile.roles?.[0];
      if (role === 'teacher') {
        navigate(createPageUrl('TeacherDashboard'));
      }
    }
  }, [userProfile, isAuthenticated, navigate]);

  // Redirect super_admin to PlatformDashboard
  useEffect(() => {
    if (userProfile && isAuthenticated) {
      const role = userProfile.active_role || userProfile.roles?.[0];
      if (role === 'super_admin') {
        navigate(createPageUrl('PlatformDashboard'));
      }
    }
  }, [userProfile, isAuthenticated, navigate]);

  // Load dashboard data when user is available
  useEffect(() => {
    logger.debug('Home useEffect triggered:', { hasProfile: !!userProfile, isAuthenticated, authLoading });
    if (userProfile && isAuthenticated) {
      logger.debug('Calling loadDashboardData for role:', userProfile.primary_role);
      loadDashboardData(userProfile);
    } else {
      logger.debug('Not loading data:', {
        hasProfile: !!userProfile,
        isAuth: isAuthenticated,
        authLoading
      });
    }
  }, [userProfile, isAuthenticated]);

  // Reload data when parent changes selected date
  useEffect(() => {
    if (userProfile?.primary_role === 'parent' && isAuthenticated && dashboardData) {
      // Only reload if we have existing data (not first load)
      loadDashboardData(userProfile, selectedDate);
    }
  }, [selectedDate]);

  // Load branches for branch_admin
  useEffect(() => {
    if (userProfile && isAuthenticated) {
      const role = userProfile.active_role || userProfile.roles?.[0];
      if (role === 'branch_admin' && userProfile.branch_ids?.length > 0) {
        loadBranches();
      }
    }
  }, [userProfile, isAuthenticated]);

  // Track last activity time and refresh session when returning from idle
  const lastActivityRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const idleTime = Date.now() - lastActivityRef.current;
        const idleThreshold = 60 * 1000; // 1 minute

        logger.debug('Page became visible', { idleTimeMs: idleTime });

        // If we've been idle for more than the threshold, refresh the session
        if (idleTime > idleThreshold && !isRefreshingRef.current && userProfile && isAuthenticated) {
          isRefreshingRef.current = true;
          logger.debug('Idle time exceeded threshold, force refreshing session...');

          try {
            const result = await forceRefreshSession();
            if (result.success) {
              logger.debug('Session refreshed after idle, reloading data...');
              // Reload dashboard data after session refresh
              loadDashboardData(userProfile);
            } else if (!result.session) {
              logger.debug('No valid session after refresh');
              // Session is gone, auth context will handle redirect
            }
          } catch (err) {
            logger.error('Failed to refresh session after idle:', err);
          } finally {
            isRefreshingRef.current = false;
          }
        }
      }
      lastActivityRef.current = Date.now();
    };

    // Track user activity
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [userProfile, isAuthenticated]);

  const loadBranches = async () => {
    try {
      const { data: allBranches, error } = await supabase
        .from('branches')
        .select('*')
        .in('id', userProfile.branch_ids || []);

      if (error) throw error;

      const branchList = allBranches || [];
      setBranches(branchList);

      if (branchList.length > 0) {
        const currentId = userProfile.current_branch_id || userProfile.branch_ids[0];
        const current = branchList.find(b => b.id === currentId) || branchList[0];
        setCurrentBranch(current);
      }
    } catch (e) {
      logger.error('Failed to load branches:', e);
    }
  };

  const loadDashboardData = async (userData, dateOverride = null) => {
    logger.debug('loadDashboardData START', { role: userData?.primary_role, date: dateOverride });
    const role = userData.primary_role || 'parent';
    logger.debug('User role:', role);
    const data = {};

    // Increment request ID to track this specific request
    const requestId = ++loadRequestIdRef.current;

    // Master timeout for the entire data load operation (20s to handle slow networks)
    // Use clearable timeout to avoid uncaught promise rejection when data loads fast
    let timeoutId = null;
    let timeoutReject = null;
    const masterTimeout = new Promise((_, reject) => {
      timeoutReject = reject;
      timeoutId = setTimeout(() => {
        // Only reject if this is still the current request
        if (loadRequestIdRef.current === requestId) {
          reject(new Error('Dashboard data load timeout'));
        }
      }, 20000);
    });

    // Helper to clear timeout and prevent uncaught rejection
    const clearMasterTimeout = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    try {
      setLoading(true);
      logger.debug('Loading set to true');

      if (role === 'super_admin') {
        // Super admin dashboard: Fetch all data in parallel for efficiency
        const [
          { data: allTenants, error: tenantsError },
          { data: allChildren, error: childrenError },
          { data: allBranches, error: branchesError },
          { data: allUsers, error: usersError }
        ] = await Promise.race([
          Promise.all([
            supabase.from('tenants').select('*'),
            supabase.from('children').select('*'),
            supabase.from('branches').select('*'),
            supabase.from('app_users').select('*')
          ]),
          masterTimeout
        ]);

        if (tenantsError || childrenError || branchesError || usersError) {
          logger.error('Error loading super admin data:', { tenantsError, childrenError, branchesError, usersError });
          toast.error('Failed to load some super admin data.');
        }

        const tenants = allTenants || [];
        const branches = allBranches || [];
        const children = allChildren || [];
        const users = allUsers || [];

        // --- Corrected Calculations ---

        // Total Tenants
        data.tenants = tenants;
        data.activeCount = tenants.filter(t => t.subscription_status === 'active').length;

        // Total Branches
        data.branches = branches;
        data.totalBranches = branches.length;

        // Monthly Revenue (Corrected)
        data.revenue = tenants.reduce((sum, t) => {
          const price = t.custom_price || (t.subscription_type === 'premium' ? 200 : 100);
          const branchCountForTenant = branches.filter(b => b.tenant_id === t.id).length;
          // If a tenant has no branches, count it as 1 for revenue purposes.
          const effectiveBranchCount = Math.max(branchCountForTenant, 1);
          return sum + (price * effectiveBranchCount);
        }, 0);
        
        // Orphaned Records (Comprehensive Calculation from OrphanedRecords.jsx)
        const validBranchIds = branches.map(b => b.id);
        const validTenantIds = tenants.map(t => t.id);

        const orphanedChildren = children.filter(c => !c.branch_id || !validBranchIds.includes(c.branch_id)).length;
        
        const parents = users.filter(u => u.roles?.includes('parent'));
        const orphanedParents = parents.filter(p => !children.some(c => Array.isArray(c.parent_ids) && c.parent_ids.includes(p.id))).length;

        // Orphaned tenants = tenants with no branches that have branch_admins
        const orphanedTenants = tenants.filter(t => {
          const tenantBranches = branches.filter(b => b.tenant_id === t.id);
          if (tenantBranches.length === 0) return true;
          // Check if any branch in this tenant has a branch_admin
          return !tenantBranches.some(b =>
            users.some(u => u.roles?.includes('branch_admin') && Array.isArray(u.branch_ids) && u.branch_ids.includes(b.id))
          );
        }).length;

        const orphanedBranches = branches.filter(b => {
          if (!b.tenant_id || !validTenantIds.includes(b.tenant_id)) return true;
          // Check if branch has any branch_admin
          return !users.some(u => u.roles?.includes('branch_admin') && Array.isArray(u.branch_ids) && u.branch_ids.includes(b.id));
        }).length;

        const teachers = users.filter(u => u.roles?.includes('teacher'));
        const orphanedTeachers = teachers.filter(t => !t.branch_ids || !Array.isArray(t.branch_ids) || t.branch_ids.length === 0).length;

        data.orphanedCount = orphanedChildren + orphanedParents + orphanedTenants + orphanedBranches + orphanedTeachers;

      } else if (role === 'branch_admin') {
        // Branch admin dashboard - OPTIMIZED with single RPC call
        const branchId = userData.current_branch_id || userData.branch_ids?.[0];
        if (branchId) {
          // Use format() for local timezone consistency with DailyUpdates.jsx
          const today = format(new Date(), 'yyyy-MM-dd');

          // Single optimized RPC call fetches all branch admin dashboard data
          const { data: dashboardResult, error: dashboardError } = await supabase.rpc(
            'get_branch_admin_dashboard_data',
            {
              p_branch_id: branchId,
              p_query_date: today
            }
          );

          if (dashboardError) {
            logger.error('Error fetching branch admin dashboard data:', dashboardError);
            throw dashboardError;
          }

          // Extract data from RPC result
          const result = dashboardResult || {};
          const branchData = result.branch;
          const groups = result.groups || [];
          const activeChildren = result.children || [];
          const teachers = result.teachers || [];
          const todayUpdates = result.todayUpdates || [];
          const todayAbsences = result.todayAbsences || [];
          const isClosureDay = result.isClosureDay || false;
          const parents = result.parents || [];

          // Get criteria schema from branch settings (must match DailyUpdates.jsx keys!)
          let criteriaSchema = [
            { key: 'mood', label: 'Nastrój' },
            { key: 'eating', label: 'Jedzenie' },
            { key: 'nap', label: 'Sen' }
          ];
          if (branchData?.settings?.daily_update_schema?.length > 0) {
            criteriaSchema = branchData.settings.daily_update_schema;
          }
          logger.debug('[BranchAdmin] Criteria schema:', criteriaSchema.map(c => c.key));

          // Create a set of absent child IDs
          const absentChildIds = new Set(todayAbsences.map(a => a.child_id));

          // Calculate completion stats per group
          const numCriteria = criteriaSchema.length;
          const groupCompletionStats = {};

          groups.forEach(group => {
            const groupChildren = activeChildren.filter(c => c.group_id === group.id);
            const groupUpdates = todayUpdates.filter(u => u.group_id === group.id);

            // If closure day, 100% complete
            if (isClosureDay) {
              groupCompletionStats[group.id] = {
                completionPercent: 100,
                isClosureDay: true,
                totalChildren: groupChildren.length
              };
              return;
            }

            // Filter out children with reported absences
            const eligibleChildren = groupChildren.filter(c => !absentChildIds.has(c.id));
            const fieldsPerChild = 1 + numCriteria; // attendance + criteria
            const totalFields = eligibleChildren.length * fieldsPerChild;

            let filledFields = 0;
            let presentCount = 0;
            eligibleChildren.forEach(child => {
              const update = groupUpdates.find(u => u.child_id === child.id);
              if (update) {
                // Count attendance field
                if (update.attendance === true || update.attendance === false) {
                  filledFields += 1;
                }
                // Track present children
                if (update.attendance === true) {
                  presentCount += 1;
                }
                // If absent (attendance === false): count as complete (no criteria needed)
                // If present (attendance === true): count actual filled criteria
                if (update.attendance === false) {
                  // Absent children are considered complete - add all criteria fields
                  filledFields += numCriteria;
                } else if (update.attendance === true && update.criteria_values) {
                  criteriaSchema.forEach(criterion => {
                    if (update.criteria_values[criterion.key]) {
                      filledFields += 1;
                    }
                  });
                }
              }
            });

            const completionPercent = totalFields > 0
              ? Math.round((filledFields / totalFields) * 100)
              : (eligibleChildren.length === 0 ? 100 : 0);

            logger.debug('[BranchAdmin] Group completion calc:', {
              groupId: group.id,
              groupName: group.name,
              eligibleChildren: eligibleChildren.length,
              totalFields,
              filledFields,
              completionPercent,
              groupUpdatesCount: groupUpdates.length
            });

            groupCompletionStats[group.id] = {
              completionPercent,
              isClosureDay: false,
              totalChildren: groupChildren.length,
              eligibleChildren: eligibleChildren.length,
              absentChildren: groupChildren.length - eligibleChildren.length,
              presentChildren: presentCount
            };
          });

          data.currentBranch = branchData;
          data.groups = groups;
          data.children = activeChildren;
          data.teachers = teachers;
          data.parents = parents;
          data.groupCompletionStats = groupCompletionStats;
          data.isClosureDay = isClosureDay;
        } else {
          data.currentBranch = null;
          data.groups = [];
          data.children = [];
          data.teachers = [];
          data.parents = [];
          data.groupCompletionStats = {};
          data.isClosureDay = false;
        }

      } else if (role === 'teacher') {
        // Teacher dashboard - OPTIMIZED with single RPC call
        const today = format(new Date(), 'yyyy-MM-dd');

        const { data: dashboardResult, error: dashboardError } = await supabase.rpc(
          'get_teacher_dashboard_data',
          {
            p_teacher_id: userData.id,
            p_query_date: today
          }
        );

        if (dashboardError) {
          logger.error('Error fetching teacher dashboard data:', dashboardError);
          throw dashboardError;
        }

        const result = dashboardResult || {};
        data.groups = result.groups || [];
        data.children = result.children || [];
        data.todayUpdates = result.todayUpdates || [];
        data.completionRate = result.completionRate || 0;

      } else if (role === 'parent') {
        // Parent dashboard - OPTIMIZED with single RPC call
        // Use format() for local timezone consistency with DailyUpdates.jsx
        const today = format(new Date(), 'yyyy-MM-dd');
        const queryDate = dateOverride || today;

        // Single optimized RPC call fetches all parent dashboard data
        const { data: dashboardResult, error: dashboardError } = await supabase.rpc(
          'get_parent_dashboard_data',
          {
            p_user_id: userData.id,
            p_query_date: queryDate
          }
        );

        if (dashboardError) {
          logger.error('Error fetching parent dashboard data:', dashboardError);
          throw dashboardError;
        }

        // Extract data from RPC result
        const result = dashboardResult || {};
        data.children = result.children || [];
        data.groups = result.groups || [];
        data.todayUpdates = result.todayUpdates || [];
        data.absences = result.absences || [];
        data.displayDate = queryDate;

        // Filter unread announcements client-side
        data.announcements = (result.announcements || [])
          .filter(ann => !ann.read_by?.includes(userData.id))
          .slice(0, 3);

        // Build unread conversations list using RPC-provided counts
        const conversations = result.conversations || [];
        const unreadCounts = result.unreadCounts || {};

        data.messages = conversations
          .filter(conv => unreadCounts[conv.id] > 0)
          .map(conv => ({
            id: conv.id,
            child_id: conv.child_id,
            child: conv.child,
            subject: conv.subject,
            created_date: conv.last_message_at,
            unreadCount: unreadCounts[conv.id],
            read: false
          }))
          .slice(0, 3);

        // Create a lookup map for branch badges by slug
        const badgeMap = {};
        (result.badges || []).forEach(badge => {
          badgeMap[badge.slug] = badge;
        });
        data.branchBadges = badgeMap;

        // Branch and tenant settings
        if (result.branch) {
          data.currentBranch = result.branch;
          data.tenantSettings = result.branch.settings || result.branch.tenant_settings || {};
        }

        // Photos still need consent check - fetch separately only if needed
        if (data.children.length > 0) {
          const groupIds = [...new Set(data.children.map(c => c.group_id).filter(Boolean))];
          if (groupIds.length > 0) {
            // Run consent and photos in parallel
            const [consentsResult, photosResult] = await Promise.all([
              supabase
                .from('consents')
                .select('granted')
                .eq('user_id', userData.id)
                .eq('consent_type', 'photo_sharing_internal')
                .eq('granted', true)
                .is('revoked_at', null)
                .maybeSingle(),
              supabase
                .from('photos')
                .select('id, file_url, created_at')
                .in('group_id', groupIds)
                .order('created_at', { ascending: false })
                .limit(6)
            ]);
            data.photos = consentsResult.data ? (photosResult.data || []) : [];
          } else {
            data.photos = [];
          }
        } else {
          data.photos = [];
        }
      }

      // Clear the timeout since data loaded successfully
      clearMasterTimeout();

      // Only update state if this is still the current request (not stale)
      if (loadRequestIdRef.current === requestId) {
        setDashboardData(data);
        logger.debug('Dashboard data set');
      } else {
        logger.debug('Skipping stale dashboard data update');
      }
    } catch (error) {
      // Clear timeout on error too
      clearMasterTimeout();

      // Skip error handling for stale requests
      if (loadRequestIdRef.current !== requestId) {
        logger.debug('Ignoring error from stale request');
        return;
      }

      logger.error('Error loading dashboard:', error);

      // Check if this is a timeout error - indicates Supabase client is stuck
      if (error.message?.includes('timeout')) {
        logger.debug('Dashboard load timed out - attempting session refresh...');
        toast.info('Ładowanie trwa długo, ponawiam...', { duration: 4000 });

        // Try to force refresh the session - retry once before giving up
        let refreshSuccess = false;
        try {
          const result = await forceRefreshSession();
          refreshSuccess = result?.success;
          if (!refreshSuccess) {
            await new Promise((r) => setTimeout(r, 2000));
            const retryResult = await forceRefreshSession();
            refreshSuccess = retryResult?.success;
          }
        } catch (refreshErr) {
          logger.error('Session refresh after timeout failed:', refreshErr);
        }
        if (!refreshSuccess) {
          toast.error('Sesja wygasła. Zaloguj się ponownie.');
        }
      } else {
        toast.error('Błąd ładowania danych');
      }
    } finally {
      // Only update loading state if this is still the current request
      if (loadRequestIdRef.current === requestId) {
        logger.debug('loadDashboardData COMPLETE, setLoading(false)');
        setLoading(false);
      }
    }
  };

  const openAbsenceDialog = (child) => {
    setSelectedChildForAbsence(child);
    setAbsenceDate(format(new Date(), 'yyyy-MM-dd'));
    setAbsenceReason('');
    setAbsenceDialogOpen(true);
  };

  const handleMarkAbsence = async () => {
    if (!selectedChildForAbsence || !absenceDate) return;

    try {
      setSavingAbsence(true);

      const today = format(new Date(), 'yyyy-MM-dd');
      const isPlanned = absenceDate > today;
      const reporterName = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() || 'Rodzic';

      await reportAbsenceMutation.mutateAsync({
        child_id: selectedChildForAbsence.id,
        absence_date: absenceDate,
        reason: absenceReason || null,
        reported_by: user.id,
        is_planned: isPlanned,
        reporterName
      });

      setAbsenceDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['child-absences'] });
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Error marking absence:', error);
      toast.error('Nie udało się zgłosić nieobecności: ' + error.message);
    } finally {
      setSavingAbsence(false);
    }
  };

  // Open cancel-absence confirmation modal (called when user clicks trash on an absence)
  const openCancelAbsenceModal = (absence, child) => {
    setAbsenceToCancel({
      id: absence.id,
      absence_date: absence.absence_date,
      childName: [child?.first_name, child?.last_name].filter(Boolean).join(' ').trim() || 'dziecka'
    });
  };

  // Confirm cancel absence (called from modal)
  const handleConfirmCancelAbsence = async () => {
    if (!absenceToCancel) return;
    try {
      await cancelAbsenceMutation.mutateAsync(absenceToCancel.id);
      setAbsenceToCancel(null);
      queryClient.invalidateQueries({ queryKey: ['child-absences'] });
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Error cancelling absence:', error);
    }
  };

  // New message dialog handlers
  const openNewMessageDialog = (child) => {
    setSelectedChildForMessage(child);
    setMessageSubject('');
    setMessageContent('');
    setNewMessageDialogOpen(true);
  };

  const handleSendNewMessage = async () => {
    if (!selectedChildForMessage || !messageContent.trim()) return;

    setSendingMessage(true);
    try {
      const child = selectedChildForMessage;

      // Get the group info to find teacher IDs
      const { data: group } = await supabase
        .from('groups')
        .select('id, main_teacher_id, teacher_ids')
        .eq('id', child.group_id)
        .single();

      // Create a new conversation
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert({
          branch_id: child.branch_id,
          group_id: child.group_id,
          child_id: child.id,
          parent_id: userProfile?.id,
          teacher_ids: group?.teacher_ids || (group?.main_teacher_id ? [group.main_teacher_id] : []),
          subject: messageSubject.trim() || `Wiadomość dot. ${child.first_name}`,
          status: 'active',
          last_message_at: new Date().toISOString()
        })
        .select()
        .single();

      if (convError) throw convError;

      // Send the first message
      const { error: msgError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_id: userProfile?.id,
          content: messageContent.trim()
        });

      if (msgError) throw msgError;

      toast.success('Wiadomość została wysłana');
      setNewMessageDialogOpen(false);
      setSelectedChildForMessage(null);
      setMessageSubject('');
      setMessageContent('');
    } catch (e) {
      logger.error('Failed to send message:', e);
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSendingMessage(false);
    }
  };

  // Swipe gesture handlers for date navigation
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      // Swipe left = go to next day
      const d = new Date(selectedDate);
      d.setDate(d.getDate() + 1);
      const today = format(new Date(), 'yyyy-MM-dd');
      const newDate = format(d, 'yyyy-MM-dd');
      if (newDate <= today) {
        changeDate(newDate, 'left');
      }
    } else if (isRightSwipe) {
      // Swipe right = go to previous day
      const d = new Date(selectedDate);
      d.setDate(d.getDate() - 1);
      changeDate(format(d, 'yyyy-MM-dd'), 'right');
    }
  };

  // Open background picker for a child
  const openBackgroundPicker = (child) => {
    setBackgroundPickerChild(child);
    setBackgroundPickerOpen(true);
  };

  // Open dietary edit dialog
  const openDietaryDialog = (child) => {
    setDietaryDialogChild(child);
    setDietaryFormData({
      dietary_restrictions: child.dietary_restrictions || [],
      dietary_notes: child.dietary_notes || ''
    });
    setDietaryDialogOpen(true);
  };

  // Save dietary changes
  const handleSaveDietary = async () => {
    if (!dietaryDialogChild) return;
    setSavingDietary(true);
    try {
      const { error } = await supabase
        .from('children')
        .update({
          dietary_restrictions: dietaryFormData.dietary_restrictions,
          dietary_notes: dietaryFormData.dietary_notes
        })
        .eq('id', dietaryDialogChild.id);

      if (error) throw error;

      // Update local dashboard data
      setDashboardData(prev => {
        if (!prev?.children) return prev;
        return {
          ...prev,
          children: prev.children.map(c =>
            c.id === dietaryDialogChild.id
              ? { ...c, dietary_restrictions: dietaryFormData.dietary_restrictions, dietary_notes: dietaryFormData.dietary_notes }
              : c
          )
        };
      });

      toast.success('Zapisano zmiany dietetyczne');
      setDietaryDialogOpen(false);
    } catch (e) {
      logger.error('Failed to save dietary info:', e);
      toast.error('Nie udało się zapisać');
    } finally {
      setSavingDietary(false);
    }
  };

  // Enrollment handlers
  const handleEnroll = async (childId, costElementId, costElementName) => {
    const key = `${childId}-${costElementId}`;
    setEnrollmentLoading(prev => ({ ...prev, [key]: true }));
    try {
      await enrollChild.mutateAsync({ childId, costElementId, userId: user?.id });
      toast.success(`Zapisano na: ${costElementName}`);
    } catch (e) {
      logger.error('Failed to enroll:', e);
      toast.error(e.message || 'Nie udało się zapisać');
    } finally {
      setEnrollmentLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleUnenroll = async (childId, costElementId, costElementName, branchId) => {
    const key = `${childId}-${costElementId}`;
    setEnrollmentLoading(prev => ({ ...prev, [key]: true }));
    try {
      await unenrollChild.mutateAsync({ childId, costElementId, branchId });
      toast.success(`Wypisano z: ${costElementName}. Zmiana obowiązuje od końca okresu rozliczeniowego.`);
    } catch (e) {
      logger.error('Failed to unenroll:', e);
      toast.error('Nie udało się wypisać');
    } finally {
      setEnrollmentLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleCancelUnenrollment = async (childId, costElementId, costElementName) => {
    const key = `${childId}-${costElementId}`;
    setEnrollmentLoading(prev => ({ ...prev, [key]: true }));
    try {
      await cancelUnenrollment.mutateAsync({ childId, costElementId });
      toast.success(`Anulowano wypisanie z: ${costElementName}`);
    } catch (e) {
      logger.error('Failed to cancel unenrollment:', e);
      toast.error('Nie udało się anulować wypisania');
    } finally {
      setEnrollmentLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Open announcement in dialog and mark as read
  const handleOpenAnnouncement = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementDialogOpen(true);

    // Mark as read if not already read
    const isUnread = !announcement.read_by?.includes(user?.id);
    if (isUnread && user?.id) {
      try {
        const currentReadBy = announcement.read_by || [];
        const { error } = await supabase
          .from('announcements')
          .update({ read_by: [...currentReadBy, user.id] })
          .eq('id', announcement.id);

        if (error) {
          logger.error('Error marking announcement as read:', error);
        } else {
          // Remove from local state (since dashboard only shows unread)
          setDashboardData(prev => {
            if (!prev?.announcements) return prev;
            return {
              ...prev,
              announcements: prev.announcements.filter(a => a.id !== announcement.id)
            };
          });
        }
      } catch (err) {
        logger.error('Error marking announcement as read:', err);
      }
    }
  };

  // Open conversation sheet and mark messages as read
  const handleOpenMessage = async (message) => {
    // Build conversation object from message data
    // Note: message.id is actually the conversation ID (see how data.messages is built)
    const conversation = {
      id: message.id,  // This is the conversation_id
      child: message.child,
      subject: message.subject
    };

    setSelectedConversation(conversation);
    setReplyContent('');
    setChatSheetOpen(true);

    // Mark all messages in conversation as read using optimized RPC
    if (user?.id && message.id) {
      try {
        await supabase.rpc('mark_conversation_messages_read', {
          p_conversation_id: message.id,
          p_user_id: user.id
        });

        // Remove from local state (since dashboard only shows unread)
        setDashboardData(prev => {
          if (!prev?.messages) return prev;
          return {
            ...prev,
            messages: prev.messages.filter(m => m.id !== message.id)
          };
        });
      } catch (err) {
        logger.error('Error marking messages as read:', err);
      }
    }
  };

  // Handle closing chat sheet
  const handleChatSheetClose = (open) => {
    if (!open) {
      setChatSheetOpen(false);
      setSelectedConversation(null);
      setReplyContent('');
    }
  };

  // Handle sending reply
  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedConversation?.id) return;

    try {
      setSendingReply(true);
      await sendMessage.mutateAsync({
        conversationId: selectedConversation.id,
        content: replyContent.trim()
      });
      setReplyContent('');
      refetchMessages();

      // Scroll to bottom
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      logger.error('Error sending reply:', err);
      toast.error('Nie udało się wysłać wiadomości');
    } finally {
      setSendingReply(false);
    }
  };

  // Handle deleting conversation
  const handleDeleteConversation = async () => {
    if (!selectedConversation?.id) return;

    try {
      setDeletingConversation(true);
      await deleteConversation.mutateAsync(selectedConversation.id);
      setDeleteDialogOpen(false);
      setChatSheetOpen(false);
      setSelectedConversation(null);
    } catch (err) {
      logger.error('Error deleting conversation:', err);
    } finally {
      setDeletingConversation(false);
    }
  };

  const handleAddTenantAdmin = async () => {
    if (!tenantAdminForm.email || !tenantAdminForm.branch_id) {
      toast.error('Wypelnij adres email i wybierz oddzial');
      return;
    }

    try {
      setSavingTenantAdmin(true);

      // Get tenant_id from the selected branch
      const selectedBranch = dashboardData?.branches?.find(b => b.id === tenantAdminForm.branch_id);
      const tenantId = selectedBranch?.tenant_id;

      const { data, error } = await supabase.functions.invoke('create-branch-admin', {
        body: {
          email: tenantAdminForm.email,
          first_name: tenantAdminForm.first_name,
          last_name: tenantAdminForm.last_name,
          phone: tenantAdminForm.phone,
          branch_id: tenantAdminForm.branch_id,
          tenant_id: tenantId
        }
      });

      if (error) throw error;

      // Check email sending status
      if (data && !data.emailSent) {
        logger.error('Email sending failed:', { emailError: data.emailError });
        toast.warning(`Administrator dodany, ale email nie zostal wyslany. ${data.emailError || ''}`);
      } else if (data && data.emailSent) {
        toast.success('Administrator dodany i zaproszenie wyslane!');
      } else {
        toast.success('Administrator dodany pomyslnie');
      }

      logger.debug('Create branch admin result completed');

      setAddTenantAdminDialogOpen(false);
      setTenantAdminForm({ first_name: '', last_name: '', email: '', phone: '', branch_id: '' });

      // Reload data
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Error adding branch admin:', error);
      toast.error('Blad dodawania administratora: ' + error.message);
    } finally {
      setSavingTenantAdmin(false);
    }
  };

  // Cleanup orphaned auth users
  const handleCleanupOrphanedUsers = async () => {
    try {
      setCleaningUpUsers(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Brak autoryzacji');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cleanup-orphaned-users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Usunięto ${result.deleted_users?.length || 0} osieroconych użytkowników`);
        if (result.deleted_users?.length > 0) {
          logger.debug('Deleted orphaned users count:', result.deleted_users.length);
        }
      } else {
        toast.error(result.error || 'Błąd podczas czyszczenia');
      }
    } catch (error) {
      logger.error('Cleanup error:', error);
      toast.error('Błąd: ' + error.message);
    } finally {
      setCleaningUpUsers(false);
    }
  };

  const handleAddTenant = async () => {
    if (!tenantForm.tenant_name || !tenantForm.admin_email) {
      toast.error('Please fill in tenant name and administrator email');
      return;
    }

    try {
      setSavingTenant(true);

      // Create tenant
      const { data: tenant, error: tenantError } = await supabase
        .from('tenants')
        .insert({
          name: tenantForm.tenant_name,
          address: tenantForm.tenant_address,
          contact_first_name: tenantForm.tenant_contact_first_name,
          contact_last_name: tenantForm.tenant_contact_last_name,
          contact_email: tenantForm.tenant_contact_email,
          contact_phone: tenantForm.tenant_contact_phone,
          subscription_type: tenantForm.subscription_type,
          subscription_status: 'active',
          schema_type: tenantForm.schema_type
        })
        .select()
        .single();

      if (tenantError) throw tenantError;

      // If single branch schema, create the branch automatically
      if (tenantForm.schema_type === 'single_branch' && tenant) {
        const { error: branchError } = await supabase
          .from('branches')
          .insert({
            tenant_id: tenant.id,
            name: `${tenantForm.tenant_name} - Main Branch`,
            address: tenantForm.tenant_address,
            contact_name: `${tenantForm.tenant_contact_first_name} ${tenantForm.tenant_contact_last_name}`,
            contact_email: tenantForm.tenant_contact_email,
            contact_phone: tenantForm.tenant_contact_phone
          });

        if (branchError) logger.error('Error creating branch:', branchError);
      }

      // Create tenant admin using Edge Function
      const { data: adminData, error: adminError } = await supabase.functions.invoke('create-tenant-admin', {
        body: {
          email: tenantForm.admin_email,
          firstName: tenantForm.admin_first_name,
          lastName: tenantForm.admin_last_name,
          tenantId: tenant.id
        }
      });

      if (adminError) {
        logger.error('Error creating admin:', adminError);
        toast.warning('Tenant created but failed to create administrator');
      } else {
        // Check email sending status
        if (adminData && !adminData.emailSent) {
          logger.error('Admin email sending failed:', { emailError: adminData.emailError });
          toast.warning(`Tenant and administrator created, but invitation email failed to send. ${adminData.emailError || 'Check Resend API key.'}`);
        } else if (adminData && adminData.emailSent) {
          toast.success('Tenant and administrator created successfully! Invitation email sent.');
        } else {
          toast.success('Tenant and administrator created successfully');
        }
        logger.debug('Create admin result completed');
      }

      setAddTenantDialogOpen(false);
      setTenantForm({
        schema_type: 'single_branch',
        tenant_name: '',
        tenant_address: '',
        tenant_contact_first_name: '',
        tenant_contact_last_name: '',
        tenant_contact_email: '',
        tenant_contact_phone: '',
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        admin_phone: '',
        subscription_type: 'standard'
      });

      // Reload data
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Error adding tenant:', error);
      toast.error('Failed to add tenant: ' + error.message);
    } finally {
      setSavingTenant(false);
    }
  };

  // Group photo handling (branch_admin)
  const handleGroupPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setSelectedGroupImage(imageUrl);
    setCropGroupDialogOpen(true);
  };

  const handleGroupCropComplete = async (croppedFile) => {
    setUploadingGroupPhoto(true);
    setCropGroupDialogOpen(false);

    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      const fileName = `${branchId}-group-${Date.now()}.jpg`;
      const filePath = `group-photos/${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, croppedFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath);

      setGroupForm(prev => ({ ...prev, picture_url: publicUrl }));
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      logger.error('Photo upload error:', error);
      toast.error('Failed to upload photo: ' + error.message);
    } finally {
      setUploadingGroupPhoto(false);
      if (selectedGroupImage) {
        URL.revokeObjectURL(selectedGroupImage);
        setSelectedGroupImage(null);
      }
    }
  };

  // Handler for adding a group (branch_admin)
  const handleAddGroup = async () => {
    if (!groupForm.name?.trim()) {
      toast.error('Nazwa grupy jest wymagana');
      return;
    }

    setSavingGroup(true);
    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];

      const { data: newGroup, error } = await supabase
        .from('groups')
        .insert({
          branch_id: branchId,
          name: groupForm.name,
          picture_url: groupForm.picture_url || null,
          color_scheme: groupForm.color_scheme,
          main_teacher_id: groupForm.main_teacher_id || null
        })
        .select()
        .single();

      if (error) throw error;

      // Update secondary teachers if any
      if (groupForm.secondary_teacher_ids.length > 0) {
        for (const teacherId of groupForm.secondary_teacher_ids) {
          const { data: teacher } = await supabase
            .from('app_users')
            .select('group_ids')
            .eq('id', teacherId)
            .single();

          const currentGroupIds = teacher?.group_ids || [];
          await supabase
            .from('app_users')
            .update({ group_ids: [...currentGroupIds, newGroup.id] })
            .eq('id', teacherId);
        }
      }

      // Update main teacher's group_ids
      if (groupForm.main_teacher_id) {
        const { data: mainTeacher } = await supabase
          .from('app_users')
          .select('group_ids')
          .eq('id', groupForm.main_teacher_id)
          .single();

        const currentGroupIds = mainTeacher?.group_ids || [];
        await supabase
          .from('app_users')
          .update({ group_ids: [...currentGroupIds, newGroup.id] })
          .eq('id', groupForm.main_teacher_id);
      }

      // Assign children to group if any
      if (groupForm.child_ids.length > 0) {
        await supabase
          .from('children')
          .update({ group_id: newGroup.id })
          .in('id', groupForm.child_ids);
      }

      // Seed default Plan dnia (Przedziały + Aktywności)
      try {
        await seedDefaultDailyPlanForGroup(newGroup.id);
      } catch (seedErr) {
        logger.error('Default plan dnia seed failed for new group', { groupId: newGroup.id, seedErr });
        toast.error('Grupa utworzona, ale nie udało się dodać domyślnego planu dnia. Możesz dodać go ręcznie.');
      }

      toast.success('Grupa została utworzona pomyślnie!');
      setAddGroupDialogOpen(false);
      setGroupForm({
        name: '',
        picture_url: '',
        color_scheme: '#3b82f6',
        main_teacher_id: '',
        secondary_teacher_ids: [],
        child_ids: []
      });

      // Reload data
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Error adding group:', error);
      toast.error('Nie udało się utworzyć grupy: ' + error.message);
    } finally {
      setSavingGroup(false);
    }
  };

  // Child photo handling (branch_admin)
  const handleChildPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setSelectedChildImage(imageUrl);
    setCropChildDialogOpen(true);
  };

  const handleChildCropComplete = async (croppedFile) => {
    setUploadingChildPhoto(true);
    setCropChildDialogOpen(false);

    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      
      // For new children (no ID yet), upload directly and store URL in form
      // For existing children, use uploadAvatarPhoto to update database
      if (childForm.id) {
        // Existing child - use uploadAvatarPhoto to update database
        const { publicUrl } = await uploadAvatarPhoto({
          file: croppedFile,
          folder: 'avatars',
          entityId: childForm.id,
          table: 'children',
          column: 'photo_url',
          branchId: branchId
        });
        setChildForm(prev => ({ ...prev, photo_url: publicUrl }));
      } else {
        // New child - upload directly with branch_id folder structure
        const fileName = `${childForm.child_first_name || 'child'}-${Date.now()}.jpg`;
        const filePath = `avatars/${branchId}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('user-uploads')
          .upload(filePath, croppedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('user-uploads')
          .getPublicUrl(filePath);

        setChildForm(prev => ({ ...prev, photo_url: publicUrl }));
      }

      toast.success('Photo uploaded successfully!');
    } catch (error) {
      logger.error('Photo upload error:', error);
      toast.error('Failed to upload photo: ' + error.message);
    } finally {
      setUploadingChildPhoto(false);
      if (selectedChildImage) {
        URL.revokeObjectURL(selectedChildImage);
        setSelectedChildImage(null);
      }
    }
  };

  // Parent child avatar editing handlers
  const handleParentChildPhotoSelect = (e, child) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Proszę wybrać plik obrazu');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Zdjęcie musi być mniejsze niż 5MB');
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setSelectedChildForAvatarEdit(child);
    setChildAvatarImage(imageUrl);
    setCropChildAvatarDialogOpen(true);
  };

  const handleParentChildAvatarCropComplete = async (croppedFile) => {
    if (!selectedChildForAvatarEdit) return;

    setUploadingChildAvatar(true);
    setCropChildAvatarDialogOpen(false);

    try {
      // Get branch_id from child or user profile
      const branchId = selectedChildForAvatarEdit.branch_id || 
                      userProfile?.current_branch_id || 
                      userProfile?.branch_ids?.[0];
      
      await uploadAvatarPhoto({
        file: croppedFile,
        folder: 'avatars',
        entityId: selectedChildForAvatarEdit.id,
        table: 'children',
        column: 'photo_url',
        branchId: branchId
      });

      toast.success('Zdjęcie dziecka zaktualizowane!');

      // Reload data to show updated photo
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Child avatar upload error:', error);
      toast.error('Nie udało się przesłać zdjęcia: ' + error.message);
    } finally {
      setUploadingChildAvatar(false);
      if (childAvatarImage) {
        URL.revokeObjectURL(childAvatarImage);
        setChildAvatarImage(null);
      }
      setSelectedChildForAvatarEdit(null);
    }
  };

  // Handler for adding a child (branch_admin) - uses edge function to also invite parent
  const handleAddChild = async () => {
    const required = ['child_first_name', 'child_last_name', 'parent_first_name', 'parent_last_name', 'parent_email'];
    if (required.some(field => !childForm[field])) {
      toast.error('Proszę wypełnić wszystkie wymagane pola dla dziecka i rodzica.');
      return;
    }

    // PESEL is optional, but if provided must be 11 digits
    if (childForm.pesel && childForm.pesel.length !== 11) {
      toast.error('PESEL musi składać się z 11 cyfr.');
      return;
    }

    setSavingChild(true);
    const toastId = toast.loading('Dodawanie dziecka i zapraszanie rodzica...');

    try {
      const branchId = userProfile.current_branch_id || userProfile.branch_ids?.[0];
      if (!branchId) {
        toast.error("Nie można określić bieżącego oddziału.", { id: toastId });
        setSavingChild(false);
        return;
      }

      const limitCheck = wouldExceedFreemiumLimit(
        currentBranch,
        dashboardData?.children?.length ?? 0,
        1
      );
      if (!limitCheck.allowed) {
        toast.error(limitCheck.message, { id: toastId });
        setSavingChild(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('create-child-and-invite-parent', {
        body: { ...childForm, branch_id: branchId },
      });

      if (data?.error) throw new Error(data.error);
      if (error) throw new Error(await getFunctionErrorMessage(error));

      toast.success('Dziecko dodane i rodzic zaproszony pomyślnie!', { id: toastId });
      setAddChildDialogOpen(false);
      setChildForm({
        child_first_name: '',
        child_last_name: '',
        date_of_birth: '',
        pesel: '',
        group_id: '',
        parent_first_name: '',
        parent_last_name: '',
        parent_email: '',
        parent_phone: ''
      });

      // Reload data
      if (userProfile) {
        loadDashboardData(userProfile);
      }
    } catch (error) {
      logger.error('Error adding child:', error);
      toast.error(error?.message || 'Błąd podczas dodawania dziecka.', { id: toastId });
    } finally {
      setSavingChild(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuthenticated || !userProfile) {
    return null;
  }

  // Show loading while fetching dashboard data
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const role = userProfile.primary_role || 'parent';
  const data = dashboardData || {};

  // Child Enrollment Section Component (renders enrollment options for a single child)
  const ChildEnrollmentSection = ({ child }) => {
    const { data: enrollableCostElements, isLoading: loadingElements } = useEnrollableCostElements(
      child.branch_id,
      { enabled: !!child.branch_id }
    );
    const { data: childEnrollments, isLoading: loadingEnrollments } = useChildEnrollments(
      child.id,
      { enabled: !!child.id }
    );

    if (loadingElements || loadingEnrollments) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-500 text-sm">Ładowanie...</span>
        </div>
      );
    }

    if (!enrollableCostElements || enrollableCostElements.length === 0) {
      return (
        <div className="text-center py-6 text-slate-500">
          <Coins className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Brak dostępnych opcji zapisu</p>
          <p className="text-xs text-slate-400 mt-1">Przedszkole nie udostępniło żadnych zajęć dodatkowych.</p>
        </div>
      );
    }

    // Create a map of current enrollments by cost element ID
    const enrollmentMap = {};
    childEnrollments?.forEach(e => {
      enrollmentMap[e.cost_element_id] = e;
    });

    return (
      <div className="space-y-3">
        <p className="text-xs text-slate-500 mb-2">
          Opcjonalne zajęcia i usługi do samodzielnego zapisu.
        </p>

        {enrollableCostElements.map(element => {
          const enrollment = enrollmentMap[element.id];
          const isEnrolled = !!enrollment;
          // With immediate unenrollment, effective_until means the child has been unenrolled
          const isUnenrolled = enrollment?.effective_until != null;
          const loadingKey = `${child.id}-${element.id}`;
          const isLoading = enrollmentLoading[loadingKey];

          // Calculate current enrollment count for capacity display
          const currentEnrollments = element.current_enrollment_count || 0;
          const hasCapacity = element.max_capacity != null;
          const isFull = hasCapacity && currentEnrollments >= element.max_capacity;

          return (
            <div
              key={element.id}
              className={cn(
                "p-3 rounded-lg border transition-colors",
                isEnrolled && !isUnenrolled
                  ? 'border-green-200 bg-green-50'
                  : isUnenrolled
                  ? 'border-slate-200 bg-slate-50'
                  : 'border-slate-200 bg-white'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-slate-900 text-sm">{element.name}</h4>
                    {isEnrolled && !isUnenrolled && (
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        Zapisany
                      </Badge>
                    )}
                    {isUnenrolled && (
                      <Badge className="bg-slate-100 text-slate-600 text-xs">
                        Wypisany od {format(new Date(enrollment.effective_until), 'd MMM', { locale: pl })}
                      </Badge>
                    )}
                  </div>
                  {element.description && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{element.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs">
                    {(() => {
                      const pricing = calculateMonthlyEstimate(element);
                      const schedule = getScheduleDescription(element);
                      return (
                        <>
                          <span className="font-medium text-blue-600">
                            {pricing.priceLabel}
                          </span>
                          {schedule && (
                            <span className="text-slate-400">
                              {schedule}
                            </span>
                          )}
                        </>
                      );
                    })()}
                    {hasCapacity && (
                      <span className={cn("text-xs", isFull ? 'text-red-600' : 'text-slate-400')}>
                        Zajęte: {currentEnrollments}/{element.max_capacity}
                        {isFull && ' (brak miejsc)'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0">
                  {isEnrolled ? (
                    isUnenrolled ? (
                      // Child was unenrolled - show restore button
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelUnenrollment(child.id, element.id, element.name)}
                        disabled={isLoading || isFull}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50 h-8 text-xs"
                      >
                        {isLoading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          'Przywróć'
                        )}
                      </Button>
                    ) : (
                      // Currently enrolled - show unenroll button
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnenroll(child.id, element.id, element.name, child.branch_id)}
                        disabled={isLoading}
                        className="text-red-600 border-red-300 hover:bg-red-50 h-8 text-xs"
                      >
                        {isLoading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <><X className="w-3 h-3 mr-1" />Wypisz</>
                        )}
                      </Button>
                    )
                  ) : (
                    // Not enrolled - show enroll button
                    <Button
                      size="sm"
                      onClick={() => handleEnroll(child.id, element.id, element.name)}
                      disabled={isLoading || isFull}
                      className="bg-gradient-to-r from-green-500 to-emerald-600 text-white h-8 text-xs"
                    >
                      {isLoading ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <><Check className="w-3 h-3 mr-1" />Zapisz</>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Daily Plan View Component (shows today's schedule for parent view)
  const DailyPlanView = ({ groupId }) => {
    const todayStr = formatLocalDate(new Date());
    const { data: plan, isLoading: planLoading } = useDailyPlan(groupId, todayStr);
    const { data: timeframes, isLoading: timeframesLoading } = useTimeframes(groupId);

    const isLoading = planLoading || timeframesLoading;

    if (!groupId) {
      return (
        <div className="text-center py-6 text-slate-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Dziecko nie jest przypisane do grupy</p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          <span className="ml-2 text-slate-500 text-sm">Ładowanie...</span>
        </div>
      );
    }

    if (!timeframes || timeframes.length === 0) {
      return (
        <div className="text-center py-6 text-slate-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Plan dnia nie jest jeszcze skonfigurowany</p>
          <p className="text-xs text-slate-400 mt-1">Nauczyciele wkrótce go przygotują.</p>
        </div>
      );
    }

    // Check if plan is published
    if (!plan?.is_published) {
      return (
        <div className="text-center py-6 text-slate-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Plan dnia nie został jeszcze opublikowany</p>
          <p className="text-xs text-slate-400 mt-1">Sprawdź ponownie później.</p>
        </div>
      );
    }

    const hasAnyActivities = plan?.items && plan.items.length > 0;

    if (!hasAnyActivities) {
      return (
        <div className="text-center py-6 text-slate-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Brak zaplanowanych aktywności na dziś</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Plan na dziś - {format(new Date(), 'd MMMM', { locale: pl })}
        </p>
        <div className="space-y-2">
          {timeframes.map(timeframe => {
            const items = plan?.items?.filter(item => item.timeframe_id === timeframe.id) || [];

            return (
              <div
                key={timeframe.id}
                className="flex items-start gap-3 py-2 px-3 rounded-lg bg-gradient-to-br from-slate-50 to-white border border-slate-100"
              >
                <div className="flex items-center gap-2 text-slate-600 min-w-[100px]">
                  <Clock className="w-3.5 h-3.5 flex-shrink-0 text-blue-500" />
                  <span className="text-xs font-semibold">{timeframe.name}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 items-center flex-1">
                  {items.length > 0 ? (
                    items.map((item, index) => {
                      const activityName = item.custom_activity_name || item.activity?.name;
                      const activityColor = item.activity?.color || '#6366f1';

                      return (
                        <Badge
                          key={item.id || index}
                          variant="secondary"
                          className="text-xs font-medium px-2 py-0.5"
                          style={{
                            backgroundColor: `${activityColor}15`,
                            color: activityColor,
                            borderColor: `${activityColor}30`
                          }}
                        >
                          {activityName}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-xs text-slate-300 italic">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render different dashboards based on role
  return (
    <>
      <OfflineIndicator />
      
      {/* Super Admin Dashboard */}
      {role === 'super_admin' && (
        <div className="space-y-6">
          <div className="space-y-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">SuperAdmin Dashboard</h1>
              <p className="text-slate-500">Overview of all Tenants</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                onClick={() => setAddTenantAdminDialogOpen(true)}
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <Plus className="w-4 h-4 mr-2" />
                Administrator
              </Button>
              <Button
                onClick={() => setAddTenantDialogOpen(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Placówka
              </Button>
              <Button
                onClick={handleCleanupOrphanedUsers}
                disabled={cleaningUpUsers}
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50"
              >
                {cleaningUpUsers ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Wyczyść osieroconych
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            {/* Total Tenants */}
            <Card
              onClick={() => navigate('/Tenants')}
              className="cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden border-0"
            >
              <CardContent className="p-0 h-full">
                <div
                  className="p-6 text-white h-full"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">{t('totalTenants') || 'Organizacje'}</p>
                      <p className="text-3xl font-bold mt-2">{data.tenants?.length || 0}</p>
                    </div>
                    <Building2 className="w-12 h-12 text-blue-200 opacity-80" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Orphaned Records */}
            <Card
              onClick={() => navigate('/OrphanedRecords')}
              className="cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden border-0"
            >
              <CardContent className="p-0 h-full">
                <div
                  className="p-6 text-white h-full"
                  style={{
                    background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">{t('orphanedRecords') || 'Orphaned Records'}</p>
                      <p className="text-3xl font-bold mt-2">{data.orphanedCount || 0}</p>
                    </div>
                    <AlertCircle className="w-12 h-12 text-orange-200 opacity-80" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Branch Admin Dashboard */}
      {role === 'branch_admin' && (
        <div className="space-y-6">
          {/* Header - branch switching is in the sidebar/context switcher */}
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Pulpit Administratora Oddziału</h1>
            <p className="text-slate-500">
              Zarządzaj oddziałem{currentBranch && ` ${currentBranch.display_name || currentBranch.name}`}
            </p>
          </div>

          {/* Kreator Banner - only shows until all setup tasks are complete */}
          <KreatorBanner progress={kreatorProgress} />

          {/* Quick Actions - Indigo/Violet theme matching sidebar */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={() => setAddGroupDialogOpen(true)}
              className="bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-md"
            >
              <Users className="w-4 h-4 mr-2" />
              Dodaj grupę
            </Button>
            <Button
              onClick={() => setAddChildDialogOpen(true)}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-md"
            >
              <Baby className="w-4 h-4 mr-2" />
              Dodaj dziecko
            </Button>
            {isModuleEnabled('jadlospis') && (
            <Button
              onClick={() => navigate('/MenuManagement')}
              className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 shadow-md"
            >
              <Utensils className="w-4 h-4 mr-2" />
              Jadłospis
            </Button>
            )}
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Total Groups */}
            <Card
              onClick={() => navigate('/Groups')}
              className="cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden border-0"
            >
              <CardContent className="p-0 h-full">
                <div
                  className="p-6 text-white h-full"
                  style={{
                    background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">Grupy</p>
                      <p className="text-3xl font-bold mt-2">{data.groups?.length || 0}</p>
                    </div>
                    <Users className="w-12 h-12 text-blue-200 opacity-80" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Children */}
            <Card
              onClick={() => navigate('/Children')}
              className="cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden border-0"
            >
              <CardContent className="p-0 h-full">
                <div
                  className="p-6 text-white h-full"
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm font-medium">Dzieci</p>
                      <p className="text-3xl font-bold mt-2">{data.children?.length || 0}</p>
                    </div>
                    <Baby className="w-12 h-12 text-emerald-200 opacity-80" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Total Teachers */}
            <Card
              onClick={() => navigate('/Teachers')}
              className="cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden border-0"
            >
              <CardContent className="p-0 h-full">
                <div
                  className="p-6 text-white h-full"
                  style={{
                    background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">Nauczyciele</p>
                      <p className="text-3xl font-bold mt-2">{data.teachers?.length || 0}</p>
                    </div>
                    <Users className="w-12 h-12 text-purple-200 opacity-80" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Parents */}
            <Card
              onClick={() => navigate('/Parents')}
              className="cursor-pointer transform transition-all duration-200 hover:scale-105 hover:shadow-xl overflow-hidden border-0"
            >
              <CardContent className="p-0 h-full">
                <div
                  className="p-6 text-white h-full"
                  style={{
                    background: 'linear-gradient(135deg, #f97316 0%, #f59e0b 100%)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm font-medium">Rodzice</p>
                      <p className="text-3xl font-bold mt-2">{data.parents?.length || 0}</p>
                    </div>
                    <UserPlus className="w-12 h-12 text-orange-200 opacity-80" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Groups List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Grupy w oddziale
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.groups && data.groups.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.groups.map(group => {
                    // Count children in this group
                    const childrenInGroup = data.children?.filter(c => c.group_id === group.id) || [];

                    // Count unique parent accounts for children in this group
                    const parentIds = new Set();
                    childrenInGroup.forEach(child => {
                      if (Array.isArray(child.parent_ids)) {
                        child.parent_ids.forEach(pid => parentIds.add(pid));
                      }
                    });

                    // Get group color for gradient
                    const groupColor = group.color_scheme || '#3b82f6';

                    // Get completion stats for this group
                    const completionStats = data.groupCompletionStats?.[group.id] || { completionPercent: 0 };
                    const completionPercent = completionStats.completionPercent;

                    // Determine completion badge color
                    const getCompletionBadgeColor = (percent) => {
                      if (completionStats.isClosureDay) return 'bg-slate-500';
                      if (percent >= 80) return 'bg-green-500';
                      if (percent >= 50) return 'bg-amber-500';
                      return 'bg-red-500';
                    };

                    return (
                      <Card
                        key={group.id}
                        className="cursor-pointer hover:shadow-xl transition-all overflow-hidden border-0"
                        onClick={() => navigate(`/Groups?group=${group.id}`)}
                      >
                        <CardContent className="p-0">
                          {/* Gradient Header */}
                          <div
                            className="p-4 text-white"
                            style={{
                              background: `linear-gradient(135deg, ${groupColor} 0%, color-mix(in srgb, ${groupColor} 70%, #000) 100%)`
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                {/* Group picture */}
                                {group.picture_url ? (
                                  <img
                                    src={group.picture_url}
                                    alt={group.name}
                                    className="w-14 h-14 rounded-xl object-cover border-2 border-white/30 shadow-lg shrink-0"
                                  />
                                ) : (
                                  <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/30 shrink-0">
                                    <Users className="w-7 h-7 text-white" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-lg">{group.name}</h3>
                                  {group.description && (
                                    <p className="text-xs text-white/80 mt-1">{group.description}</p>
                                  )}
                                </div>
                              </div>
                              {/* Completion badge */}
                              <div
                                className={`${getCompletionBadgeColor(completionPercent)} px-2 py-1 rounded-full text-xs font-bold text-white shadow-sm shrink-0`}
                                title={completionStats.isClosureDay
                                  ? 'Dzień wolny'
                                  : `Kompletność: ${completionPercent}%`
                                }
                              >
                                {completionStats.isClosureDay ? '—' : `${completionPercent}%`}
                              </div>
                            </div>
                          </div>

                          {/* Stats */}
                          <div className="p-4 space-y-2">
                            <div
                              className="flex items-center justify-between p-2 rounded-lg"
                              style={{ backgroundColor: `${groupColor}15` }}
                            >
                              <div className="flex items-center gap-2">
                                <Baby className="w-4 h-4" style={{ color: groupColor }} />
                                <span className="text-sm text-slate-700">Obecność</span>
                              </div>
                              <span className="text-sm font-semibold" style={{ color: groupColor }}>
                                {completionStats.isClosureDay
                                  ? `—/${childrenInGroup.length}`
                                  : `${completionStats.presentChildren ?? 0}/${childrenInGroup.length}`
                                }
                              </span>
                            </div>

                            <div
                              className="flex items-center justify-between p-2 rounded-lg"
                              style={{ backgroundColor: `${groupColor}10` }}
                            >
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4" style={{ color: groupColor }} />
                                <span className="text-sm text-slate-700">Rodzice</span>
                              </div>
                              <span className="text-sm font-semibold" style={{ color: groupColor }}>{parentIds.size}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  icon={Users}
                  title="Brak grup"
                  description="Utwórz grupy, aby zorganizować dzieci i przypisać nauczycieli"
                />
              )}
            </CardContent>
          </Card>

          {/* Analytics Dashboard */}
          {currentBranch && (
            <>
              <Separator className="my-8" />
              <AnalyticsDashboard
                branchId={currentBranch.id}
                isBranchAdmin={true}
                isJadlospisEnabled={currentBranch?.settings?.modules?.jadlospis !== false}
                isMediaEnabled={currentBranch?.settings?.modules?.media !== false}
              />
            </>
          )}
        </div>
      )}

      {/* Parent Dashboard */}
      {role === 'parent' && data.children && data.children.length > 0 && (
        <div className="space-y-6">
          {/* Header with swipeable date navigation */}
          <div
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className="select-none"
          >
            <h1 className="text-2xl font-bold text-slate-900">
              Cześć, {userProfile?.first_name || 'Rodzic'}!
            </h1>
            {/* Swipeable Date Navigation */}
            <div className="flex items-center justify-center gap-3 mt-3 py-3 px-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl">
              <button
                onClick={() => {
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() - 1);
                  changeDate(formatLocalDate(d), 'right');
                }}
                className="p-2 rounded-full hover:bg-white text-slate-400 hover:text-slate-600 transition-all hover:shadow-md active:scale-95"
                title="Poprzedni dzień"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div className="flex-1 text-center">
                <p className="text-lg font-semibold text-slate-700 capitalize">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pl-PL', { weekday: 'long' })}
                </p>
                <p className="text-sm text-slate-500">
                  {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                {selectedDate === todayStr && (
                  <Badge className="bg-blue-500 text-white mt-1">Dziś</Badge>
                )}
              </div>
              {selectedDate === todayStr ? (
                <div className="p-2 w-10" /> // Spacer for alignment
              ) : (
                <button
                  onClick={() => {
                    const d = new Date(selectedDate);
                    d.setDate(d.getDate() + 1);
                    const newDate = format(d, 'yyyy-MM-dd');
                    if (newDate <= todayStr) {
                      changeDate(newDate, 'left');
                    }
                  }}
                  className="p-2 rounded-full hover:bg-white text-slate-400 hover:text-slate-600 transition-all hover:shadow-md active:scale-95"
                  title="Następny dzień"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </div>
            {selectedDate !== todayStr && (
              <div className="text-center mt-2">
                <button
                  onClick={() => setSelectedDate(todayStr)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  ← Wróć do dziś
                </button>
              </div>
            )}
            <p className="text-xs text-slate-400 text-center mt-2 md:hidden">
              Przesuń aby zmienić dzień
            </p>
          </div>

          {/* Children Cards and Calendar - side by side on wide screens, wraps if needed */}
          <div
            className={`flex flex-wrap gap-6 w-full max-w-5xl mx-auto items-stretch justify-center transition-all duration-150 ease-out ${
              dateTransition.phase === 'exit'
                ? `opacity-0 ${dateTransition.direction === 'left' ? '-translate-x-4' : 'translate-x-4'}`
                : 'opacity-100 translate-x-0'
            }`}
          >
            {/* Children Cards - Carousel for multiple, single card for one */}
            <div className="w-full max-w-md flex-1 min-w-[320px]">
              <ChildCarousel
                activeIndex={activeChildIndex}
                onIndexChange={setActiveChildIndex}
              >
              {data.children.map(child => {
              const todayUpdate = data.todayUpdates?.find(u => u.child_id === child.id);
              const childGroup = data.groups?.find(g => g.id === child.group_id);

              const groupColor = childGroup?.color_scheme || '#3b82f6';
              // Default criteria schema (same as DailyUpdates.jsx fallback)
              const DEFAULT_CRITERIA_SCHEMA = [
                { key: 'mood', label: 'Nastrój', icon: '😊' },
                { key: 'eating', label: 'Jedzenie', icon: '🍽️' },
                { key: 'nap', label: 'Sen', icon: '😴' }
              ];
              const criteriaSchema = data.tenantSettings?.daily_update_schema || DEFAULT_CRITERIA_SCHEMA;

              logger.debug('Home - Child daily update:', {
                hasTodayUpdate: !!todayUpdate,
                hasCriteriaValues: !!todayUpdate?.criteria_values,
                hasBadges: !!todayUpdate?.badges,
                hasNotes: !!todayUpdate?.notes
              });

              // Get badge info for upper right corner - now shows all badges with tooltips
              const legacyBadgeIcons = {
                star: '⭐',
                helper: '🤝',
                creative: '🎨',
                heart: '❤️',
                trophy: '🏆',
                friend: '🌟',
                book: '📚',
                music: '🎵',
                brave: '🦁'
              };

              // Build array of badge objects with full info
              const badgesWithInfo = (todayUpdate?.badges || []).map(badgeSlug => {
                const branchBadge = data.branchBadges?.[badgeSlug];
                if (branchBadge) {
                  return {
                    slug: badgeSlug,
                    emoji: branchBadge.emoji,
                    name: branchBadge.name,
                    description: branchBadge.description
                  };
                }
                // Fallback for legacy badges
                return {
                  slug: badgeSlug,
                  emoji: legacyBadgeIcons[badgeSlug] || '🏅',
                  name: badgeSlug,
                  description: null
                };
              });

              // Get background preset for this child
              const bgPreset = getBackground(child.id);

              return (
                <Card key={child.id} className="group relative overflow-hidden border border-slate-200 shadow-lg rounded-3xl h-full flex flex-col">
                  {/* Badges in upper right corner - pulsing glow and confetti on click */}
                  {badgesWithInfo.length > 0 && (
                    <div className="absolute top-3 right-3 z-10 flex items-center gap-1.5">
                      {badgesWithInfo.map((badge, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="relative min-w-[48px] min-h-[48px] flex items-center justify-center bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-500 text-white rounded-full active:scale-95 hover:scale-110 transition-all duration-200 touch-manipulation ring-4 ring-amber-300/70 ring-offset-2 ring-offset-white/80 animate-badge-glow-pulse"
                          aria-label={`Odznaka: ${badge.name}`}
                          onClick={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            const x = (rect.left + rect.width / 2) / window.innerWidth;
                            const y = (rect.top + rect.height / 2) / window.innerHeight;
                            const colors = ['#fbbf24', '#f59e0b', '#fcd34d', '#d97706', '#6366f1', '#8b5cf6', '#ec4899', '#f472b6'];
                            const fire = (override = {}) => confetti({
                              particleCount: 100,
                              spread: 70,
                              origin: { x, y },
                              colors,
                              startVelocity: 35,
                              gravity: 0.9,
                              scalar: 1.15,
                              ticks: 250,
                              drift: 0.5,
                              ...override
                            });
                            fire();
                            setTimeout(() => fire({ particleCount: 80, spread: 100, scalar: 1.05 }), 80);
                            setTimeout(() => fire({ particleCount: 60, spread: 55, startVelocity: 28 }), 160);
                            setBadgeCelebration({ badge });
                          }}
                        >
                          <span className="absolute inset-0 rounded-full bg-amber-400/50 blur-xl animate-pulse pointer-events-none" style={{ animationDuration: '2s' }} aria-hidden />
                          <span className="relative text-2xl drop-shadow-md">{badge.emoji}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Background picker button - upper left */}
                  <button
                    onClick={() => openBackgroundPicker(child)}
                    className="absolute top-3 left-3 z-10 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white text-slate-400 hover:text-slate-600 shadow-md transition-all opacity-70 group-hover:opacity-100"
                    title="Zmień tło"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  <CardHeader
                    className={cn("pb-4 pt-5 relative", getGradientClasses(bgPreset))}
                    style={getBackgroundStyle(bgPreset)}
                  >
                    <div className="flex items-center gap-5">
                      <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-600 rounded-full blur-md opacity-50 group-hover:opacity-75 transition duration-300"></div>
                        <Avatar className="relative h-24 w-24 ring-4 ring-white shadow-2xl">
                          <AvatarImage src={child.photo_url} />
                          <AvatarFallback className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-3xl font-bold">
                            {child.first_name?.[0]}{child.last_name?.[0]}
                          </AvatarFallback>
                        </Avatar>
                        {/* Camera icon for photo upload */}
                        <label
                          className="absolute bottom-0 right-0 p-2 bg-gradient-to-br from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-full cursor-pointer shadow-xl transition-all hover:scale-110"
                          title="Zmień zdjęcie"
                        >
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleParentChildPhotoSelect(e, child)}
                            className="hidden"
                            disabled={uploadingChildAvatar}
                          />
                          {uploadingChildAvatar && selectedChildForAvatarEdit?.id === child.id ? (
                            <Loader2 className="w-4 h-4 text-white animate-spin" />
                          ) : (
                            <Camera className="w-4 h-4 text-white" />
                          )}
                        </label>
                      </div>
                      <div className={cn("flex-1", badgesWithInfo.length > 0 && (badgesWithInfo.length > 1 ? "pr-24" : "pr-14"))}>
                        <CardTitle className="text-2xl font-bold text-white mb-1 drop-shadow-lg" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
                          {child.first_name} {child.last_name}
                        </CardTitle>
                        <p className="text-sm text-white/95 font-semibold mb-3 drop-shadow-md" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                          {childGroup?.name || 'Brak grupy'}
                        </p>
                        {/* Quick action buttons */}
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 px-4 text-xs font-semibold bg-white/90 backdrop-blur-sm hover:bg-white text-orange-600 border-orange-200 hover:border-orange-400 shadow-md hover:shadow-lg transition-all"
                            onClick={() => openAbsenceDialog(child)}
                          >
                            <Calendar className="w-4 h-4 mr-1.5" />
                            Nieobecność
                          </Button>
                          {chatEnabled && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-9 px-4 text-xs font-semibold bg-white/90 backdrop-blur-sm hover:bg-white text-blue-600 border-blue-200 hover:border-blue-400 shadow-md hover:shadow-lg transition-all"
                            onClick={() => openNewMessageDialog(child)}
                          >
                            <MessageSquare className="w-4 h-4 mr-1.5" />
                            Wiadomość
                          </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 pt-5 pb-6 bg-gradient-to-b from-white via-slate-50/50 to-slate-50">
                    {/* Swipeable Content with Touch Gestures */}
                    {(() => {
                      const tabs = [
                        { key: 'oceny', label: 'Oceny', icon: '📊' },
                        { key: 'plan', label: 'Plan dnia', icon: '📅' },
                        ...(rozliczeniaEnabled ? [{ key: 'zapisy', label: 'Zapisy', icon: '📝' }] : []),
                        { key: 'dieta', label: 'Dieta', icon: '🥗' }
                      ];
                      const rawMode = getViewMode(child.id);
                      const effectiveMode = tabs.some((t) => t.key === rawMode) ? rawMode : tabs[0].key;
                      const activeIndex = tabs.findIndex((t) => t.key === effectiveMode);

                      // Swipe handling using closure variables (no hooks needed)
                      // stopPropagation prevents bubbling to ChildCarousel's swipe handlers
                      let touchStartX = null;
                      let touchEndX = null;
                      const minSwipeDistance = 50;

                      const onTouchStart = (e) => {
                        e.stopPropagation();
                        touchEndX = null;
                        touchStartX = e.targetTouches[0].clientX;
                      };

                      const onTouchMove = (e) => {
                        e.stopPropagation();
                        touchEndX = e.targetTouches[0].clientX;
                      };

                      const onTouchEnd = (e) => {
                        e.stopPropagation();
                        if (touchStartX === null || touchEndX === null) return;
                        const distance = touchStartX - touchEndX;
                        const isLeftSwipe = distance > minSwipeDistance;
                        const isRightSwipe = distance < -minSwipeDistance;

                        if (isLeftSwipe && activeIndex < tabs.length - 1) {
                          setViewMode(child.id, tabs[activeIndex + 1].key);
                        }
                        if (isRightSwipe && activeIndex > 0) {
                          setViewMode(child.id, tabs[activeIndex - 1].key);
                        }
                      };

                      return (
                        <>
                          {/* Section label with current view */}
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                              <span className="text-base">{tabs[activeIndex]?.icon}</span>
                              {tabs[activeIndex]?.label}
                            </span>
                            <span className="text-xs text-slate-400">← Przesuń →</span>
                          </div>

                          {/* Swipeable container */}
                          <div
                            className="relative overflow-hidden rounded-xl"
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                          >
                            <div
                              className="flex transition-transform duration-300 ease-out"
                              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                            >
                              {/* OCENY VIEW */}
                              <div className="w-full flex-shrink-0">
                                <div className="space-y-3 pb-1">
                                  {(() => {
                                    const hasReportedAbsence = (data.absences || []).some(
                                      a => a.child_id === child.id && a.type !== 'closure' && String(a.absence_date).slice(0, 10) === selectedDate
                                    );
                                    const isTeacherMarkedAbsent = todayUpdate?.attendance === false;
                                    const isAbsentOnSelectedDate = hasReportedAbsence || isTeacherMarkedAbsent;
                                    if (isAbsentOnSelectedDate) {
                                      const isPlanned = selectedDate > todayStr;
                                      return (
                                        <div className="flex flex-col items-center justify-center py-8 px-4 rounded-2xl bg-gradient-to-br from-amber-50 via-orange-50/80 to-amber-50 border border-amber-200/80 shadow-inner">
                                          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100/90 text-amber-600 mb-4 ring-4 ring-amber-200/50">
                                            <CalendarX2 className="h-7 w-7" strokeWidth={2} />
                                          </div>
                                          <p className="text-lg font-semibold text-amber-800 tracking-tight">
                                            Nieobecność
                                          </p>
                                          <p className="text-sm text-amber-700/90 mt-1 text-center max-w-[220px]">
                                            {isPlanned
                                              ? 'Zaplanowana nieobecność w tym dniu.'
                                              : selectedDate === todayStr
                                                ? 'Dziecko nie jest dziś w placówce.'
                                                : 'Brak ocen — dziecko nie było w placówce w ten dzień.'}
                                          </p>
                                        </div>
                                      );
                                    }
                                    return todayUpdate ? (
                                      <div className="space-y-3">
                                        {todayUpdate.criteria_values && criteriaSchema.length > 0 &&
                                         criteriaSchema.some(c => todayUpdate.criteria_values[c.key]) ? (
                                          <div className="space-y-2">
                                            {criteriaSchema.map((criterion) => {
                                              const value = todayUpdate.criteria_values[criterion.key];
                                              if (!value) return null;

                                              const emoji = value === 'positive' ? '😊' : value === 'neutral' ? '😐' : '😢';
                                              const bgGradient = value === 'positive'
                                                ? 'bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 border-l-4 border-green-400 shadow-sm hover:shadow-md'
                                                : value === 'neutral'
                                                ? 'bg-gradient-to-br from-yellow-50 via-amber-50 to-yellow-50 border-l-4 border-yellow-400 shadow-sm hover:shadow-md'
                                                : 'bg-gradient-to-br from-red-50 via-rose-50 to-red-50 border-l-4 border-red-400 shadow-sm hover:shadow-md';

                                              return (
                                                <div key={criterion.key} className={`flex items-center justify-between py-3 px-4 rounded-xl transition-all duration-200 ${bgGradient}`}>
                                                  <span className="text-sm font-bold text-slate-700">{criterion.label}</span>
                                                  <span className="text-3xl">{emoji}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="text-center py-6 text-slate-400 text-sm font-medium bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 rounded-xl border border-slate-100">
                                            Brak ocen {selectedDate === todayStr ? 'na dziś' : 'na ten dzień'}
                                          </div>
                                        )}

                                        {todayUpdate.notes && (
                                          <div className="text-sm text-slate-700 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 border-l-4 border-blue-500 p-4 rounded-xl shadow-sm">
                                            <span className="font-bold text-blue-900">📝 Nota:</span> <span className="text-slate-700 ml-2">{todayUpdate.notes}</span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-sm text-slate-400 italic text-center py-8 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 rounded-xl border border-slate-100">
                                        Brak informacji {selectedDate === todayStr ? 'na dziś' : 'na ten dzień'}
                                      </p>
                                    );
                                  })()}

                                  <WeeklySummary
                                    childId={child.id}
                                    branchId={data.currentBranch?.id}
                                    criteriaSchema={criteriaSchema}
                                    selectedDate={selectedDate}
                                    onDateChange={setSelectedDate}
                                    branchBadges={data.branchBadges}
                                  />

                                  <MonthlySummary
                                    childId={child.id}
                                    criteriaSchema={criteriaSchema}
                                  />
                                </div>
                              </div>

                              {/* PLAN DNIA VIEW */}
                              <div className="w-full flex-shrink-0">
                                <div className="pb-1">
                                  <DailyPlanView groupId={child.group_id} />
                                </div>
                              </div>

                              {/* ZAPISY VIEW - hidden when rozliczenia module disabled */}
                              {rozliczeniaEnabled && (
                              <div className="w-full flex-shrink-0">
                                <div className="pb-1">
                                  <ChildEnrollmentSection child={child} />
                                </div>
                              </div>
                              )}

                              {/* DIETA VIEW */}
                              <div className="w-full flex-shrink-0">
                                <div className="space-y-4 pb-1">
                                  {child.dietary_restrictions?.length > 0 ? (
                                    <div>
                                      <p className="text-sm font-medium text-slate-700 mb-2">Ograniczenia dietetyczne</p>
                                      <div className="flex flex-wrap gap-2">
                                        {child.dietary_restrictions.map(restriction => (
                                          <Badge key={restriction} variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                                            <AlertCircle className="w-3 h-3 mr-1" />
                                            {getDietaryLabel(restriction)}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-slate-400 text-sm">Brak ograniczeń dietetycznych</p>
                                  )}

                                  {child.dietary_notes && (
                                    <div>
                                      <p className="text-sm font-medium text-slate-700 mb-2">Notatki</p>
                                      <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                                        {child.dietary_notes}
                                      </p>
                                    </div>
                                  )}

                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openDietaryDialog(child)}
                                    className="w-full"
                                  >
                                    <Edit2 className="w-4 h-4 mr-2" />
                                    Aktualizuj informacje dietetyczne
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Dot indicators */}
                          <div className="flex justify-center items-center gap-2 mt-4">
                            {tabs.map((tab, idx) => (
                              <button
                                key={tab.key}
                                onClick={() => setViewMode(child.id, tab.key)}
                                className={cn(
                                  "transition-all duration-200 rounded-full",
                                  idx === activeIndex
                                    ? "w-6 h-2 bg-blue-500"
                                    : "w-2 h-2 bg-slate-300 hover:bg-slate-400"
                                )}
                                title={tab.label}
                              />
                            ))}
                          </div>
                        </>
                      );
                    })()}

                    {/* Show reported absences for this child (exclude closure-type absences) */}
                    {(() => {
                      const childAbsences = data.absences?.filter(a => a.child_id === child.id && a.type !== 'closure') || [];
                      if (childAbsences.length === 0) return null;

                      return (
                        <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg">
                          <p className="text-xs font-semibold text-orange-700 mb-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Zgłoszone:
                          </p>
                          <div className="space-y-0.5">
                            {childAbsences.slice(0, 2).map(absence => {
                              const absenceDate = new Date(absence.absence_date);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const isToday = absenceDate.toDateString() === today.toDateString();
                              const formattedDate = absenceDate.toLocaleDateString('pl-PL', {
                                weekday: 'short',
                                day: 'numeric',
                                month: 'short'
                              });

                              return (
                                <div key={absence.id} className="flex items-center justify-between gap-1 text-xs text-orange-800">
                                  <span className={`font-medium ${isToday ? 'text-red-600' : ''}`}>
                                    {isToday ? 'Dziś' : formattedDate}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openCancelAbsenceModal(absence, child);
                                    }}
                                    className="p-0.5 rounded hover:bg-orange-200 text-orange-500 hover:text-orange-700 transition-colors"
                                    title="Anuluj nieobecność"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              );
                            })}
                            {childAbsences.length > 2 && (
                              <p className="text-xs text-orange-500">
                                +{childAbsences.length - 2} więcej
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              );
            })}
            </ChildCarousel>
            </div>

            {/* Calendar Widget - shows closures and child absences */}
            {data.currentBranch?.id && (
              <div className="w-full max-w-md flex-1 min-w-[320px] transition-all duration-300 ease-out">
                <ClosuresCalendar
                  branchId={data.currentBranch.id}
                  childIds={data.children?.map(c => c.id) || []}
                  backgroundPreset={data.children?.[activeChildIndex] ? getBackground(data.children[activeChildIndex].id) : undefined}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Empty state for parents with no children */}
      {role === 'parent' && (!data.children || data.children.length === 0) && (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={Baby}
              title="Brak zarejestrowanych dzieci"
              description="Skontaktuj się z administratorem placówki, aby dodać profil dziecka"
            />
          </CardContent>
        </Card>
      )}

      {/* Absence Dialog */}
      <Dialog open={absenceDialogOpen} onOpenChange={setAbsenceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Zgłoś nieobecność</DialogTitle>
            {selectedChildForAbsence && (
              <p className="text-sm text-slate-500">
                Zgłaszanie nieobecności dla {selectedChildForAbsence.first_name} {selectedChildForAbsence.last_name}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="absence_date">Data *</Label>
              <Input
                id="absence_date"
                type="date"
                value={absenceDate}
                onChange={(e) => setAbsenceDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="absence_reason">Powód (opcjonalnie)</Label>
              <Input
                id="absence_reason"
                value={absenceReason}
                onChange={(e) => setAbsenceReason(e.target.value)}
                placeholder="np. Choroba, Wyjazd rodzinny"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbsenceDialogOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleMarkAbsence}
              disabled={savingAbsence || !absenceDate}
              className="bg-gradient-to-r from-orange-500 to-red-600"
            >
              {savingAbsence && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Zgłoś nieobecność
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Message Dialog */}
      <Dialog open={newMessageDialogOpen} onOpenChange={setNewMessageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Nowa wiadomość
            </DialogTitle>
            {selectedChildForMessage && (
              <p className="text-sm text-slate-500">
                Wiadomość dot. {selectedChildForMessage.first_name} {selectedChildForMessage.last_name}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="message_subject">Temat (opcjonalnie)</Label>
                <SpeechToTextButton
                  currentValue={messageSubject}
                  onResult={setMessageSubject}
                  append={false}
                  size="icon"
                />
              </div>
              <Input
                id="message_subject"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                placeholder="np. Pytanie o zajęcia"
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="message_content">Treść wiadomości *</Label>
                <SpeechToTextButton
                  currentValue={messageContent}
                  onResult={setMessageContent}
                  append
                  size="icon"
                />
              </div>
              <Textarea
                id="message_content"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Napisz swoją wiadomość..."
                rows={5}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewMessageDialogOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleSendNewMessage}
              disabled={sendingMessage || !messageContent.trim()}
              className={themeColors.headerBg}
            >
              {sendingMessage && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Send className="w-4 h-4 mr-2" />
              Wyślij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Announcement Dialog */}
      <Dialog open={announcementDialogOpen} onOpenChange={setAnnouncementDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              {selectedAnnouncement?.title || selectedAnnouncement?.subject || 'Ogłoszenie'}
            </DialogTitle>
            <p className="text-xs text-slate-400">
              {selectedAnnouncement?.created_date || selectedAnnouncement?.created_at
                ? new Date(selectedAnnouncement.created_date || selectedAnnouncement.created_at).toLocaleDateString('pl-PL', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })
                : ''}
            </p>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
              {selectedAnnouncement?.content}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setAnnouncementDialogOpen(false)}>
              Zamknij
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat Sheet - slides from bottom */}
      <Sheet open={chatSheetOpen} onOpenChange={handleChatSheetClose}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-2xl p-0 flex flex-col">
          {/* Header */}
          <SheetHeader className={cn("px-4 py-3 border-b text-white rounded-t-2xl", themeColors.headerBg)}>
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10 border-2 border-white/30">
                <AvatarImage src={selectedConversation?.child?.photo_url} />
                <AvatarFallback className="bg-white/20 text-white">
                  {selectedConversation?.child?.first_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-white text-left">
                  {selectedConversation?.child
                    ? `${selectedConversation.child.first_name} ${selectedConversation.child.last_name}`
                    : 'Rozmowa'}
                </SheetTitle>
                <p className="text-sm text-white/80 truncate">
                  {selectedConversation?.subject || 'Rozmowa z przedszkolem'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white/80 hover:text-white hover:bg-white/20"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="h-5 w-5" />
              </Button>
            </div>
          </SheetHeader>

          {/* Messages Area */}
          <div className="flex-1 overflow-hidden flex flex-col bg-slate-50">
            {/* Load more button */}
            {hasNextPage && (
              <div className="p-2 text-center border-b bg-white">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowUp className="w-4 h-4 mr-2" />
                  )}
                  Załaduj starsze wiadomości
                </Button>
              </div>
            )}

            {/* Messages */}
            <div
              ref={messagesContainerRef}
              className="flex-1 p-4 space-y-3 overflow-y-auto"
            >
              {messagesLoading && conversationMessages.length === 0 ? (
                <div className="flex justify-center py-8">
                  <Loader2 className={cn("w-8 h-8 animate-spin", themeColors.chatAccent?.replace('bg-', 'text-') || "text-slate-500")} />
                </div>
              ) : conversationMessages.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-8">
                  Brak wiadomości w tej rozmowie
                </p>
              ) : (
                conversationMessages.map(msg => {
                  const isMine = msg.sender_id === user?.id;
                  const isUnread = !msg.read_by?.includes(user?.id) && !isMine;
                  const senderName = msg.sender
                    ? `${msg.sender.first_name} ${msg.sender.last_name}`
                    : 'Nieznany';
                  const displayRole = isMine
                    ? (userProfile?.active_role || userProfile?.primary_role)
                    : (msg.sender?.active_role ?? msg.sender?.primary_role);
                  const roleLabel = displayRole ? getRoleLabel(displayRole) : '';
                  const isTeacher = displayRole === 'teacher';

                  return (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex",
                        isMine ? "justify-end" : "justify-start"
                      )}
                    >
                      <div className="flex items-end gap-2 max-w-[85%]">
                        {!isMine && (
                          <Avatar className="h-7 w-7 shrink-0">
                            <AvatarImage src={msg.sender?.avatar_url} />
                            <AvatarFallback className="text-xs bg-slate-200">
                              {msg.sender?.first_name?.charAt(0) || '?'}
                            </AvatarFallback>
                          </Avatar>
                        )}
                        <div
                          className={cn(
                            "p-3 rounded-2xl",
                            isMine
                              ? cn(themeColors.chatAccent, "text-white rounded-br-sm")
                              : "bg-white border rounded-bl-sm shadow-sm"
                          )}
                        >
                          <p className={cn(
                            "text-xs mb-1 font-medium",
                            isMine
                              ? themeColors.chatAccentLight
                              : isTeacher ? themeColors.chatAccent?.replace('bg-', 'text-') || "text-slate-500" : "text-slate-500"
                          )}>
                            {senderName} {roleLabel && `(${roleLabel})`}
                          </p>
                          <p className={cn(
                            "text-sm whitespace-pre-line",
                            isMine ? "text-white" : "text-slate-700",
                            isUnread && "font-bold"
                          )}>
                            {msg.content}
                          </p>
                          <p className={cn(
                            "text-xs mt-1",
                            isMine ? themeColors.chatAccentLight : "text-slate-400"
                          )}>
                            {format(new Date(msg.created_at), 'd MMM, HH:mm', { locale: pl })}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Reply input - fixed at bottom, above bottom nav on mobile */}
          <div className="p-4 border-t bg-white chat-reply-above-nav">
            <div className="flex gap-2">
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Napisz odpowiedź..."
                className="flex-1 min-h-[50px] max-h-[120px] resize-none"
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              />
              <SpeechToTextButton
                currentValue={replyContent}
                onResult={setReplyContent}
                append
                size="icon"
                className="self-end"
              />
              <Button
                onClick={handleSendReply}
                disabled={sendingReply || !replyContent.trim()}
                className={cn("self-end text-white", themeColors.headerBg)}
              >
                {sendingReply ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Conversation Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć rozmowę?</AlertDialogTitle>
            <AlertDialogDescription>
              Ta rozmowa zostanie usunięta dla wszystkich uczestników. Tej operacji nie można cofnąć.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingConversation}>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConversation}
              disabled={deletingConversation}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingConversation ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Usuń
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Administrator Dialog */}
      <Dialog open={addTenantAdminDialogOpen} onOpenChange={setAddTenantAdminDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Dodaj administratora</DialogTitle>
            <p className="text-sm text-slate-500">Utworz konto administratora oddzialu</p>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ta_first_name">Imie *</Label>
                <Input
                  id="ta_first_name"
                  value={tenantAdminForm.first_name}
                  onChange={(e) => setTenantAdminForm(p => ({ ...p, first_name: e.target.value }))}
                  placeholder="Jan"
                />
              </div>
              <div>
                <Label htmlFor="ta_last_name">Nazwisko *</Label>
                <Input
                  id="ta_last_name"
                  value={tenantAdminForm.last_name}
                  onChange={(e) => setTenantAdminForm(p => ({ ...p, last_name: e.target.value }))}
                  placeholder="Kowalski"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="ta_email">Adres email *</Label>
              <Input
                id="ta_email"
                type="email"
                value={tenantAdminForm.email}
                onChange={(e) => setTenantAdminForm(p => ({ ...p, email: e.target.value }))}
                placeholder="admin@przedszkole.pl"
              />
            </div>
            <div>
              <Label htmlFor="ta_phone">Numer telefonu</Label>
              <Input
                id="ta_phone"
                type="tel"
                value={tenantAdminForm.phone}
                onChange={(e) => setTenantAdminForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+48 123 456 789"
              />
            </div>
            <div>
              <Label htmlFor="ta_branch">Przypisz do oddzialu *</Label>
              <Select
                value={tenantAdminForm.branch_id}
                onValueChange={(value) => setTenantAdminForm(p => ({ ...p, branch_id: value }))}
              >
                <SelectTrigger id="ta_branch">
                  <SelectValue placeholder="Wybierz oddzial" />
                </SelectTrigger>
                <SelectContent>
                  {dashboardData?.branches?.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.display_name || branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTenantAdminDialogOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleAddTenantAdmin}
              disabled={savingTenantAdmin || !tenantAdminForm.email || !tenantAdminForm.branch_id || !tenantAdminForm.first_name || !tenantAdminForm.last_name}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {savingTenantAdmin && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Dodaj administratora
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Tenant Dialog */}
      <Dialog open={addTenantDialogOpen} onOpenChange={setAddTenantDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Tenant</DialogTitle>
            <p className="text-sm text-slate-500">Create a new tenant organization</p>
          </DialogHeader>
          <div className="space-y-6">
            {/* Schema Type */}
            <div>
              <Label htmlFor="schema_type">Organization Schema *</Label>
              <Select
                value={tenantForm.schema_type}
                onValueChange={(value) => setTenantForm(p => ({ ...p, schema_type: value }))}
              >
                <SelectTrigger id="schema_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single_branch">Single Branch (Tenant Admin = Branch Admin)</SelectItem>
                  <SelectItem value="multi_branch">Multi Branch (Separate Tenant & Branch Admins)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tenant Details */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900">Tenant Details</h3>
              <div>
                <Label htmlFor="tenant_name">Tenant Name *</Label>
                <Input
                  id="tenant_name"
                  value={tenantForm.tenant_name}
                  onChange={(e) => setTenantForm(p => ({ ...p, tenant_name: e.target.value }))}
                  placeholder="Sunshine Kindergarten"
                />
              </div>
              <div>
                <Label htmlFor="tenant_address">Address</Label>
                <Input
                  id="tenant_address"
                  value={tenantForm.tenant_address}
                  onChange={(e) => setTenantForm(p => ({ ...p, tenant_address: e.target.value }))}
                  placeholder="123 Main Street, Warsaw"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subscription_type">Subscription Plan *</Label>
                  <Select
                    value={tenantForm.subscription_type}
                    onValueChange={(value) => setTenantForm(p => ({ ...p, subscription_type: value }))}
                  >
                    <SelectTrigger id="subscription_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (100 PLN/month)</SelectItem>
                      <SelectItem value="premium">Premium (200 PLN/month)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Tenant Contact Details */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900">Tenant Contact Person</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tenant_contact_first_name">First Name</Label>
                  <Input
                    id="tenant_contact_first_name"
                    value={tenantForm.tenant_contact_first_name}
                    onChange={(e) => setTenantForm(p => ({ ...p, tenant_contact_first_name: e.target.value }))}
                    placeholder="Anna"
                  />
                </div>
                <div>
                  <Label htmlFor="tenant_contact_last_name">Last Name</Label>
                  <Input
                    id="tenant_contact_last_name"
                    value={tenantForm.tenant_contact_last_name}
                    onChange={(e) => setTenantForm(p => ({ ...p, tenant_contact_last_name: e.target.value }))}
                    placeholder="Nowak"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tenant_contact_email">Email</Label>
                  <Input
                    id="tenant_contact_email"
                    type="email"
                    value={tenantForm.tenant_contact_email}
                    onChange={(e) => setTenantForm(p => ({ ...p, tenant_contact_email: e.target.value }))}
                    placeholder="contact@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="tenant_contact_phone">Phone</Label>
                  <Input
                    id="tenant_contact_phone"
                    value={tenantForm.tenant_contact_phone}
                    onChange={(e) => setTenantForm(p => ({ ...p, tenant_contact_phone: e.target.value }))}
                    placeholder="+48 123 456 789"
                  />
                </div>
              </div>
            </div>

            {/* Administrator Details */}
            <div className="space-y-4">
              <h3 className="font-medium text-slate-900">Tenant Administrator Account</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="admin_first_name">First Name</Label>
                  <Input
                    id="admin_first_name"
                    value={tenantForm.admin_first_name}
                    onChange={(e) => setTenantForm(p => ({ ...p, admin_first_name: e.target.value }))}
                    placeholder="Jan"
                  />
                </div>
                <div>
                  <Label htmlFor="admin_last_name">Last Name</Label>
                  <Input
                    id="admin_last_name"
                    value={tenantForm.admin_last_name}
                    onChange={(e) => setTenantForm(p => ({ ...p, admin_last_name: e.target.value }))}
                    placeholder="Kowalski"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="admin_email">Email *</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    value={tenantForm.admin_email}
                    onChange={(e) => setTenantForm(p => ({ ...p, admin_email: e.target.value }))}
                    placeholder="admin@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="admin_phone">Phone</Label>
                  <Input
                    id="admin_phone"
                    value={tenantForm.admin_phone}
                    onChange={(e) => setTenantForm(p => ({ ...p, admin_phone: e.target.value }))}
                    placeholder="+48 123 456 789"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTenantDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddTenant}
              disabled={savingTenant || !tenantForm.tenant_name || !tenantForm.admin_email}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {savingTenant && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Group Dialog (branch_admin) */}
      <Dialog open={addGroupDialogOpen} onOpenChange={setAddGroupDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="add-group-description">
          <DialogHeader>
            <DialogTitle>Dodaj nową grupę</DialogTitle>
            <p id="add-group-description" className="text-sm text-slate-500">Utwórz nową grupę w swoim oddziale</p>
          </DialogHeader>

          <div className="space-y-4">
            {/* Group Name */}
            <div>
              <Label htmlFor="group_name">Nazwa grupy *</Label>
              <Input
                id="group_name"
                value={groupForm.name}
                onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="np. Słoneczka, Tęczowa Grupa"
              />
            </div>

            {/* Group Picture */}
            <div>
              <Label>Zdjęcie grupy</Label>
              <div className="flex items-center gap-6 mt-2">
                <div className="relative">
                  <Avatar className="h-24 w-24 ring-4 ring-white shadow-lg">
                    {groupForm.picture_url ? (
                      <AvatarImage src={groupForm.picture_url} />
                    ) : (
                      <AvatarFallback
                        className="text-white text-3xl font-semibold"
                        style={{ background: groupForm.color_scheme }}
                      >
                        {groupForm.name?.charAt(0)?.toUpperCase() || 'G'}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <label className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleGroupPhotoSelect}
                      className="hidden"
                      disabled={uploadingGroupPhoto}
                    />
                    {uploadingGroupPhoto ? (
                      <Loader2 className="w-4 h-4 text-white animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 text-white" />
                    )}
                  </label>
                </div>
                <div>
                  <p className="text-sm text-slate-700 font-medium">Zdjęcie grupy</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Kliknij ikonę aparatu, aby przesłać
                  </p>
                  <p className="text-xs text-slate-400">
                    Zalecany kwadratowy obraz, maks. 5MB
                  </p>
                </div>
              </div>
            </div>

            {/* Color Scheme */}
            <div>
              <Label htmlFor="color_scheme">Schemat kolorów grupy</Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  id="color_scheme"
                  type="color"
                  value={groupForm.color_scheme}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, color_scheme: e.target.value }))}
                  className="h-10 w-20 rounded border border-slate-300 cursor-pointer"
                />
                <Input
                  value={groupForm.color_scheme}
                  onChange={(e) => setGroupForm(prev => ({ ...prev, color_scheme: e.target.value }))}
                  placeholder="#3b82f6"
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Ten kolor będzie używany do identyfikacji grupy w aplikacji
              </p>
            </div>

            {/* Main Teacher */}
            <div>
              <Label htmlFor="main_teacher">Główny nauczyciel</Label>
              <Select
                value={groupForm.main_teacher_id || 'none'}
                onValueChange={(value) => setGroupForm(prev => ({ ...prev, main_teacher_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger id="main_teacher">
                  <SelectValue placeholder="Wybierz głównego nauczyciela (opcjonalnie)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Brak</SelectItem>
                  {dashboardData?.teachers?.map(teacher => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Secondary Teachers */}
            <div>
              <Label>Dodatkowi nauczyciele (opcjonalnie)</Label>
              <div className="mt-2 space-y-2">
                {dashboardData?.teachers?.filter(t => t.id !== groupForm.main_teacher_id).map(teacher => (
                  <label key={teacher.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupForm.secondary_teacher_ids.includes(teacher.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setGroupForm(prev => ({
                            ...prev,
                            secondary_teacher_ids: [...prev.secondary_teacher_ids, teacher.id]
                          }));
                        } else {
                          setGroupForm(prev => ({
                            ...prev,
                            secondary_teacher_ids: prev.secondary_teacher_ids.filter(id => id !== teacher.id)
                          }));
                        }
                      }}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">{teacher.first_name} {teacher.last_name}</span>
                  </label>
                ))}
                {(!dashboardData?.teachers || dashboardData.teachers.filter(t => t.id !== groupForm.main_teacher_id).length === 0) && (
                  <p className="text-sm text-slate-400 italic">Brak dodatkowych nauczycieli</p>
                )}
              </div>
            </div>

            {/* Children */}
            <div>
              <Label>Przypisz dzieci (opcjonalnie)</Label>
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {dashboardData?.children?.filter(c => !c.group_id).map(child => (
                  <label key={child.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupForm.child_ids.includes(child.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setGroupForm(prev => ({
                            ...prev,
                            child_ids: [...prev.child_ids, child.id]
                          }));
                        } else {
                          setGroupForm(prev => ({
                            ...prev,
                            child_ids: prev.child_ids.filter(id => id !== child.id)
                          }));
                        }
                      }}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">{child.first_name} {child.last_name}</span>
                  </label>
                ))}
                {(!dashboardData?.children || dashboardData.children.filter(c => !c.group_id).length === 0) && (
                  <p className="text-sm text-slate-400 italic p-2">Brak nieprzypisanych dzieci</p>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Wyświetlane są tylko dzieci bez przypisanej grupy
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAddGroupDialogOpen(false);
                setGroupForm({
                  name: '',
                  picture_url: '',
                  color_scheme: '#3b82f6',
                  main_teacher_id: '',
                  secondary_teacher_ids: [],
                  child_ids: []
                });
              }}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleAddGroup}
              disabled={savingGroup || !groupForm.name}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {savingGroup && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Utwórz grupę
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Crop Dialog for Group Photo */}
      {selectedGroupImage && (
        <ImageCropDialog
          open={cropGroupDialogOpen}
          onClose={() => {
            setCropGroupDialogOpen(false);
            if (selectedGroupImage) {
              URL.revokeObjectURL(selectedGroupImage);
              setSelectedGroupImage(null);
            }
          }}
          imageSrc={selectedGroupImage}
          onCropComplete={handleGroupCropComplete}
        />
      )}

      {/* Add Child Dialog (branch_admin) - same as Children.jsx */}
      <Dialog open={addChildDialogOpen} onOpenChange={setAddChildDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Dodaj nowe dziecko</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="space-y-2">
              <h4 className="font-medium">Dane dziecka</h4>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Imię"
                  value={childForm.child_first_name}
                  onChange={e => setChildForm({ ...childForm, child_first_name: e.target.value })}
                />
                <Input
                  placeholder="Nazwisko"
                  value={childForm.child_last_name}
                  onChange={e => setChildForm({ ...childForm, child_last_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="date"
                  value={childForm.date_of_birth}
                  onChange={e => setChildForm({ ...childForm, date_of_birth: e.target.value })}
                />
                <Input
                  placeholder="PESEL (opcjonalnie)"
                  value={childForm.pesel}
                  onChange={e => setChildForm({ ...childForm, pesel: e.target.value.replace(/\D/g, '').slice(0, 11) })}
                  maxLength={11}
                />
              </div>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Dane rodzica</h4>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Imię rodzica"
                  value={childForm.parent_first_name}
                  onChange={e => setChildForm({ ...childForm, parent_first_name: e.target.value })}
                />
                <Input
                  placeholder="Nazwisko rodzica"
                  value={childForm.parent_last_name}
                  onChange={e => setChildForm({ ...childForm, parent_last_name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  placeholder="Email rodzica"
                  type="email"
                  value={childForm.parent_email}
                  onChange={e => setChildForm({ ...childForm, parent_email: e.target.value })}
                />
                <Input
                  placeholder="Telefon rodzica"
                  value={childForm.parent_phone}
                  onChange={e => setChildForm({ ...childForm, parent_phone: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddChildDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleAddChild} disabled={savingChild}>
              {savingChild && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Zapisz i zaproś rodzica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Crop Dialog for Child Photo */}
      {selectedChildImage && (
        <ImageCropDialog
          open={cropChildDialogOpen}
          onClose={() => {
            setCropChildDialogOpen(false);
            if (selectedChildImage) {
              URL.revokeObjectURL(selectedChildImage);
              setSelectedChildImage(null);
            }
          }}
          imageSrc={selectedChildImage}
          onCropComplete={handleChildCropComplete}
        />
      )}

      {/* Image Crop Dialog for Parent Child Avatar Edit */}
      {childAvatarImage && (
        <ImageCropDialog
          open={cropChildAvatarDialogOpen}
          onClose={() => {
            setCropChildAvatarDialogOpen(false);
            if (childAvatarImage) {
              URL.revokeObjectURL(childAvatarImage);
              setChildAvatarImage(null);
            }
            setSelectedChildForAvatarEdit(null);
          }}
          imageSrc={childAvatarImage}
          onCropComplete={handleParentChildAvatarCropComplete}
        />
      )}

      {/* Background Picker Dialog for Child Cards */}
      <Dialog open={backgroundPickerOpen} onOpenChange={setBackgroundPickerOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-indigo-600" />
              Wybierz tło karty
            </DialogTitle>
            {backgroundPickerChild && (
              <p className="text-sm text-slate-500">
                dla {backgroundPickerChild.first_name}
              </p>
            )}
          </DialogHeader>
          <div className="py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-5 gap-3">
              {BACKGROUND_PRESET_LIST.map(preset => {
                const isSelected = backgroundPickerChild && getBackground(backgroundPickerChild.id) === preset.key;
                return (
                  <button
                    key={preset.key}
                    onClick={() => {
                      if (backgroundPickerChild) {
                        // Set background (non-blocking - saves in background)
                        setBackground(backgroundPickerChild.id, preset.key);
                        // Close dialog immediately
                        setBackgroundPickerOpen(false);
                      }
                    }}
                    className={cn(
                      "relative aspect-square rounded-xl overflow-hidden border-2 ring-2 transition-all duration-200",
                      isSelected 
                        ? "border-indigo-500 ring-indigo-200 shadow-lg scale-105" 
                        : "border-slate-200 ring-transparent hover:border-indigo-300 hover:scale-105"
                    )}
                    title={preset.name}
                  >
                    <div
                      className="w-full h-full"
                      style={getBackgroundStyle(preset.key)}
                    />
                    {isSelected && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center shadow-md">
                          <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 pt-3 border-t text-center">
              <p className="text-xs text-slate-400">
                Aktualnie: <span className="font-semibold text-slate-700">
                  {backgroundPickerChild && BACKGROUND_PRESETS[getBackground(backgroundPickerChild.id)]?.name}
                </span>
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dietary Edit Dialog */}
      <Dialog open={dietaryDialogOpen} onOpenChange={setDietaryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aktualizuj informacje dietetyczne</DialogTitle>
            {dietaryDialogChild && (
              <p className="text-sm text-slate-500">
                dla {dietaryDialogChild.first_name} {dietaryDialogChild.last_name}
              </p>
            )}
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium">Ograniczenia dietetyczne</Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {DIETARY_OPTIONS.map(option => (
                  <div key={option.key} className="flex items-center gap-2">
                    <Checkbox
                      id={`diet-home-${option.key}`}
                      checked={dietaryFormData.dietary_restrictions.includes(option.key)}
                      onCheckedChange={(checked) => {
                        setDietaryFormData(prev => ({
                          ...prev,
                          dietary_restrictions: checked
                            ? [...prev.dietary_restrictions, option.key]
                            : prev.dietary_restrictions.filter(d => d !== option.key)
                        }));
                      }}
                    />
                    <Label htmlFor={`diet-home-${option.key}`} className="cursor-pointer text-sm">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="dietaryNotesHome" className="text-sm font-medium">Dodatkowe notatki</Label>
              <Textarea
                id="dietaryNotesHome"
                value={dietaryFormData.dietary_notes}
                onChange={(e) => setDietaryFormData(prev => ({ ...prev, dietary_notes: e.target.value }))}
                placeholder="Dodatkowe informacje dietetyczne..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDietaryDialogOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={handleSaveDietary}
              disabled={savingDietary}
              className="bg-gradient-to-r from-blue-500 to-indigo-600"
            >
              {savingDietary && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Zapisz zmiany
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel absence confirmation */}
      <Dialog open={!!absenceToCancel} onOpenChange={(open) => !open && setAbsenceToCancel(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100">
                <CalendarX2 className="h-5 w-5 text-orange-600" />
              </span>
              Anuluj nieobecność
            </DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz anulować zgłoszoną nieobecność?
              {absenceToCancel && (
                <>
                  {' '}
                  Data: {new Date(absenceToCancel.absence_date).toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                  {absenceToCancel.childName && absenceToCancel.childName !== 'dziecka' ? ` – ${absenceToCancel.childName}` : ''}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setAbsenceToCancel(null)}>
              Nie, zostaw
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancelAbsence}
              disabled={cancelAbsenceMutation.isPending}
            >
              {cancelAbsenceMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Anuluj nieobecność
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Badge celebration – centered cheerful message when parent clicks badge on child card */}
      <Dialog open={!!badgeCelebration} onOpenChange={(open) => !open && setBadgeCelebration(null)}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto overflow-hidden border-0 p-0 gap-0 bg-transparent shadow-none" hideCloseButton aria-describedby={badgeCelebration ? 'badge-celebration-description' : undefined}>
          {badgeCelebration && (
            <>
              <DialogTitle className="sr-only">Gratulacje – Twoje dziecko otrzymało specjalną odznakę</DialogTitle>
              <DialogDescription id="badge-celebration-description" className="sr-only">
                Twoje dziecko otrzymało dziś specjalną odznakę: {badgeCelebration.badge.name}. {badgeCelebration.badge.description || ''}
              </DialogDescription>
              <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 p-6 pb-8 shadow-2xl ring-4 ring-amber-300/60">
                <div className="flex flex-col items-center text-center">
                  <span className="text-5xl drop-shadow-lg mb-3" role="img" aria-hidden>
                    {badgeCelebration.badge.emoji}
                  </span>
                  <h2 className="text-xl font-bold text-white drop-shadow-md mb-1">
                    Gratulacje!
                  </h2>
                  <p className="text-white/95 text-sm font-medium leading-relaxed mb-4">
                    Masz powód do dumy – Twoje dziecko otrzymało dziś specjalną odznakę!
                  </p>
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3 w-full text-left">
                    <p className="font-semibold text-white text-sm">{badgeCelebration.badge.name}</p>
                    {badgeCelebration.badge.description && (
                      <p className="text-white/90 text-xs mt-1">{badgeCelebration.badge.description}</p>
                    )}
                  </div>
                  <Button
                    className="mt-6 bg-white text-amber-700 hover:bg-amber-50 font-semibold shadow-lg"
                    onClick={() => setBadgeCelebration(null)}
                  >
                    Super!
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}